import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useParams, useSearchParams } from "react-router-dom";
import { customersApi } from "../../api/customers";
import { productsApi } from "../../api/products";
import CustomerMembershipsCard from "../../components/dashboard/CustomerMembershipsCard";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import {
  hasProjectPermission,
  normalizeProjectAccess,
} from "../../utils/projectAccess";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)", bg: "var(--wp-panel-bg, #F6F7FB)" };
const EMPTY_SUMMARY = { total: 0, active: 0, pending: 0, suspended: 0, deleted: 0 };

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    ...(withTime ? { timeStyle: "short" } : {}),
  }).format(date);
}

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency || "ARS",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function createOperationKey(prefix = "membership") {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function membershipPeriodLabel(value, unit) {
  const amount = Number(value || 1);
  const labels = { days: ["día", "días"], weeks: ["semana", "semanas"], months: ["mes", "meses"], years: ["año", "años"] };
  const pair = labels[String(unit || "months")] || ["período", "períodos"];
  return `${amount} ${amount === 1 ? pair[0] : pair[1]}`;
}

function customerInitials(customer) {
  const source = String(customer?.name || customer?.email || "C").trim();
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "C";
}

function customerActionContext(customer) {
  const value = [customer?.name, customer?.email, customer?.phone, customer?.document]
    .map((candidate) => String(candidate ?? "").trim())
    .find(Boolean);
  return value || "cliente";
}

function statusMeta(status) {
  const value = String(status || "active");
  if (value === "suspended") return { label: "Suspendido", color: "error" };
  if (value === "pending") return { label: "Pendiente", color: "warning" };
  if (value === "deleted") return { label: "Eliminado", color: "default" };
  return { label: "Activo", color: "success" };
}

const EMPTY_MEMBERSHIP_SUMMARY = { active: 0, paused: 0, expiring: 0, expired: 0, future: 0, none: 0, recentRenewals: 0 };
const EMPTY_MEMBERSHIP_PAYMENT_SUMMARY = { unpaid: 0, administrative: 0, accounted: 0 };
const EMPTY_MEMBERSHIP_USAGE_SUMMARY = { controlled: 0, available: 0, exhausted: 0, unlimited: 0 };
const EMPTY_MEMBERSHIP_REMINDERS = {
  items: [],
  summary: { total: 0, expiring: 0, expired: 0 },
  communication: { automaticExternalMessages: false },
  expiringDays: 7,
};

function membershipMeta(membership) {
  const status = String(membership?.status || "none");
  if (status === "expiring") return { label: "Próxima a vencer", color: "warning", attention: true };
  if (status === "expired") return { label: "Vencida", color: "error", attention: true };
  if (status === "future") return { label: "Futura", color: "default", attention: false };
  if (status === "paused") return { label: "Pausada", color: "warning", attention: false };
  if (status === "active") return { label: "Vigente", color: "default", attention: false };
  return { label: "Sin membresía", color: "default", attention: false };
}

function membershipPaymentMeta(payment) {
  const status = String(payment?.status || "unpaid").toLowerCase();
  if (status === "accounted") return { label: "En Gestión", color: "success" };
  if (status === "administrative" && payment?.online?.provider === "mercadopago") return { label: "Pagada online", color: "success" };
  if (status === "administrative") return { label: "Cobro administrativo", color: "info" };
  if (["pending", "in_process", "authorized"].includes(String(payment?.online?.providerStatus || "").toLowerCase())) return { label: "Pago online en proceso", color: "warning" };
  return { label: "Sin cobrar", color: "warning" };
}

function MembershipStatus({ customer, canManage, onRenew, onAssign, compact = false }) {
  const membership = customer?.membership || {};
  const meta = membershipMeta(membership);
  const paymentMeta = membershipPaymentMeta(membership.payment);
  const hasMembership = membership.status && membership.status !== "none";
  return (
    <Stack spacing={.45} alignItems={compact ? "flex-start" : undefined}>
      <Chip
        size="small"
        color={meta.color}
        variant={meta.attention ? "filled" : "outlined"}
        label={meta.label}
        sx={{ fontWeight: 850, alignSelf: "flex-start" }}
      />
      {hasMembership ? (
        <>
          <Chip
            size="small"
            color={paymentMeta.color}
            variant="outlined"
            label={paymentMeta.label}
            sx={{ fontWeight: 800, alignSelf: "flex-start" }}
          />
          <Typography variant="caption" sx={{ color: meta.attention ? (membership.status === "expired" ? "error.dark" : "warning.dark") : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.55)", fontWeight: meta.attention ? 850 : 650 }}>
            {membership.status === "future"
              ? `Comienza ${formatDate(membership.startDate)}`
              : membership.status === "paused"
                ? `Pausada desde ${formatDate(membership.pause?.current?.startDate)} · vence operativamente ${formatDate(membership.endDate)}`
                : `${membership.status === "expired" ? "Venció" : "Vence"} ${formatDate(membership.endDate)}`}
          </Typography>
        </>
      ) : null}
      {membership.renewalScheduled ? (
        <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 800 }}>
          Renovación ya programada
        </Typography>
      ) : null}
      {canManage && hasMembership && membership.membershipId && membership.renewable !== false && onRenew ? (
        <Button size="small" variant="text" startIcon={<AutorenewRoundedIcon />} onClick={() => onRenew(customer)} sx={{ px: .3, minWidth: 0, alignSelf: "flex-start" }}>
          Renovar
        </Button>
      ) : null}
      {canManage && !hasMembership && onAssign ? (
        <Button size="small" variant="text" startIcon={<CardMembershipRoundedIcon />} onClick={() => onAssign(customer)} sx={{ px: .3, minWidth: 0, alignSelf: "flex-start" }}>
          Asignar membresía
        </Button>
      ) : null}
    </Stack>
  );
}

function MembershipReminderPanel({ data, loading, canManage, onOpen, onRenew }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  const summary = data?.summary || EMPTY_MEMBERSHIP_REMINDERS.summary;
  if (!loading && !summary.total) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.6, borderColor: "rgba(243,90,58,.24)", bgcolor: "rgba(243,90,58,.025)" }}>
      <Stack spacing={1.35}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ md: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsActiveRoundedIcon sx={{ color: BRAND.orange }} />
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Seguimiento operativo de membresías</Typography>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                Avisos internos del equipo. WebProfit no envía mensajes al cliente desde estos recordatorios.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
            <Chip size="small" color="warning" label={`Próximas · ${summary.expiring || 0}`} sx={{ fontWeight: 850 }} />
            <Chip size="small" color="error" label={`Vencidas · ${summary.expired || 0}`} sx={{ fontWeight: 850 }} />
            <Chip size="small" variant="outlined" label="Aviso interno" sx={{ fontWeight: 850 }} />
          </Stack>
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center"><WebProfitSpinner size={18} /><Typography variant="body2">Actualizando recordatorios…</Typography></Stack>
        ) : (
          <Stack spacing={.8}>
            {items.slice(0, 6).map((reminder) => {
              const expired = reminder.status === "expired";
              return (
                <Box key={reminder.id} sx={{ p: 1.15, borderRadius: 2.5, bgcolor: "var(--wp-panel-surface, #fff)", border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{reminder.customer?.name || reminder.customer?.email || "Cliente"}</Typography>
                        <Chip size="small" color={expired ? "error" : "warning"} label={expired ? "Vencida" : "Próxima a vencer"} sx={{ height: 22, fontWeight: 800 }} />
                        <Chip
                          size="small"
                          color={membershipPaymentMeta(reminder.payment).color}
                          variant="outlined"
                          label={membershipPaymentMeta(reminder.payment).label}
                          sx={{ height: 22, fontWeight: 800 }}
                        />
                        <Chip size="small" variant="outlined" label="Cliente no contactado" sx={{ height: 22, fontWeight: 750 }} />
                      </Stack>
                      <Typography variant="body2" sx={{ mt: .25, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                        {reminder.item?.name || "Membresía"} · {expired ? "Venció" : "Vence"} {formatDate(reminder.endDate)}
                        {Number.isFinite(reminder.daysToExpiry) ? ` · ${expired ? `${Math.abs(reminder.daysToExpiry)} días vencida` : `${reminder.daysToExpiry} días restantes`}` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={.7} flexShrink={0}>
                      <Button size="small" variant="outlined" onClick={() => onOpen(reminder)}>Abrir ficha</Button>
                      {canManage && reminder.membershipId ? (
                        <Button size="small" variant="contained" startIcon={<AutorenewRoundedIcon />} onClick={() => onRenew(reminder)}>Renovar</Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {summary.total > items.slice(0, 6).length ? (
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 750 }}>
                Hay {summary.total - items.slice(0, 6).length} casos más. Usá los filtros de membresías para revisarlos.
              </Typography>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function priceListLabel(priceList) {
  if (!priceList || priceList.isBase) return "Lista base";
  if (priceList.status === "inactive") return `${priceList.name} (inactiva)`;
  if (priceList.status === "missing") return `${priceList.name} (no disponible)`;
  return priceList.name;
}

function DetailValue({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>
        {label}
      </Typography>
      <Typography sx={{ color: BRAND.dark, fontWeight: 750, overflowWrap: "anywhere" }}>
        {children || "—"}
      </Typography>
    </Box>
  );
}

function PriceListSelect({ customer, priceLists, disabled, onChange, compact = false }) {
  const current = customer?.priceList?.id || "base";
  const options = current !== "base" && !priceLists.some((list) => list.id === current)
    ? [{
        id: current,
        name: customer?.priceList?.name || "Lista no disponible",
        active: false,
        status: customer?.priceList?.status || "missing",
        isBase: false,
      }, ...priceLists]
    : priceLists;
  return (
    <FormControl size="small" fullWidth>
      <Select
        value={current}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ "aria-label": `Lista de precios de ${customer?.name || customer?.email || "cliente"}` }}
        sx={{ minWidth: compact ? 170 : 220, bgcolor: "var(--wp-panel-surface, #fff)", fontSize: compact ? ".78rem" : ".875rem" }}
      >
        {options.map((list) => (
          <MenuItem
            key={list.id}
            value={list.id}
            disabled={!list.active && list.id !== current}
          >
            {priceListLabel(list)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}


function priceListAdjustmentPercent(priceList) {
  const coefficient = Number(priceList?.coefficient ?? 1);
  if (!Number.isFinite(coefficient)) return 0;
  return Number(((coefficient - 1) * 100).toFixed(2));
}

function priceListAdjustmentLabel(priceList) {
  if (!priceList || priceList.isBase) return "Precio del catálogo";
  const percent = priceListAdjustmentPercent(priceList);
  if (Math.abs(percent) < 0.0001) return "Mismo precio que la lista base";
  return percent > 0 ? `Recargo ${percent}%` : `Descuento ${Math.abs(percent)}%`;
}

function priceListRoundingLabel(priceList) {
  const rounding = priceList?.rounding || {};
  const direction = rounding.direction === "up" ? " hacia arriba" : rounding.direction === "down" ? " hacia abajo" : "";
  if (rounding.mode === "step" && Number(rounding.step) > 0) return `Múltiplos de ${Number(rounding.step).toLocaleString("es-AR")}${direction}`;
  if (rounding.mode === "integer" || Number(rounding.decimals) === 0) return `Enteros${direction}`;
  return `${Number.isInteger(Number(rounding.decimals)) ? Number(rounding.decimals) : 2} decimales${direction}`;
}

function roundingPresetFromList(priceList) {
  const rounding = priceList?.rounding || {};
  if (rounding.mode === "step") {
    const step = Number(rounding.step);
    if (step === 1000) return "step1000";
    if (step === 100) return "step100";
    if (step === 10) return "step10";
    return "custom";
  }
  if (rounding.mode === "integer" || Number(rounding.decimals) === 0) return "integer";
  if (rounding.mode === "currency" && Number(rounding.decimals ?? 2) === 2) return "currency2";
  return "custom";
}

function roundingFromPreset(preset, direction, currentRounding = null) {
  const safeDirection = ["nearest", "up", "down"].includes(direction) ? direction : "nearest";
  if (preset === "custom" && currentRounding && typeof currentRounding === "object") {
    return { ...currentRounding, direction: safeDirection };
  }
  if (preset === "integer") return { mode: "integer", decimals: 0, direction: safeDirection };
  if (preset === "step10") return { mode: "step", step: 10, decimals: 2, direction: safeDirection };
  if (preset === "step100") return { mode: "step", step: 100, decimals: 2, direction: safeDirection };
  if (preset === "step1000") return { mode: "step", step: 1000, decimals: 2, direction: safeDirection };
  return { mode: "currency", decimals: 2, direction: safeDirection };
}

function previewRoundedPrice(value, rounding) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  const direction = rounding?.direction || "nearest";
  const applyDirection = (number) => direction === "up" ? Math.ceil(number - Number.EPSILON) : direction === "down" ? Math.floor(number + Number.EPSILON) : Math.round(number + Number.EPSILON);
  let next = numeric;
  if (rounding?.mode === "step" && Number(rounding.step) > 0) {
    const step = Number(rounding.step);
    next = applyDirection(next / step) * step;
  }
  const decimals = rounding?.mode === "integer" ? 0 : Number.isInteger(Number(rounding?.decimals)) ? Number(rounding.decimals) : 2;
  const factor = 10 ** decimals;
  return applyDirection(next * factor) / factor;
}

function PriceListEditorDialog({ open, priceList, currency, busy, error, onClose, onSubmit }) {
  const editing = Boolean(priceList?.id && !priceList?.isBase);
  const [form, setForm] = React.useState({
    name: "",
    adjustment: "0",
    roundingPreset: "currency2",
    roundingDirection: "nearest",
    status: "active",
  });

  React.useEffect(() => {
    if (!open) return;
    setForm({
      name: editing ? String(priceList?.name || "") : "",
      adjustment: editing ? String(priceListAdjustmentPercent(priceList)) : "0",
      roundingPreset: editing ? roundingPresetFromList(priceList) : "currency2",
      roundingDirection: editing && ["nearest", "up", "down"].includes(priceList?.rounding?.direction)
        ? priceList.rounding.direction
        : "nearest",
      status: editing && priceList?.active === false ? "inactive" : "active",
    });
  }, [editing, open, priceList]);

  const adjustment = Number(form.adjustment);
  const coefficient = 1 + (adjustment / 100);
  const rounding = roundingFromPreset(form.roundingPreset, form.roundingDirection, priceList?.rounding);
  const preview = Number.isFinite(coefficient) && coefficient > 0 ? previewRoundedPrice(1000 * coefficient, rounding) : null;
  const valid = form.name.trim().length >= 2
    && form.name.trim().length <= 80
    && String(form.adjustment).trim() !== ""
    && Number.isFinite(adjustment)
    && coefficient >= 0.01
    && coefficient <= 100;
  const assignedCustomers = Number(priceList?.assignedCustomers || 0);

  const submit = (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    onSubmit({
      name: form.name.trim(),
      coefficient: Number(coefficient.toFixed(6)),
      rounding,
      status: form.status,
    });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>
          {editing ? `Editar ${priceList?.name || "lista"}` : "Nueva lista de precios"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              La lista modifica el precio del catálogo para los clientes que la tengan asignada. Si el cliente ya está logueado, la próxima consulta de precios toma la lista vigente; al comprar, el backend vuelve a validarla.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              autoFocus
              fullWidth
              label="Nombre de la lista"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              inputProps={{ maxLength: 80 }}
              placeholder="Ej. Mayorista, Distribuidores, VIP"
              helperText="Usá un nombre que el equipo pueda reconocer al asignarlo a un cliente."
            />
            <TextField
              fullWidth
              type="number"
              label="Ajuste sobre la lista base (%)"
              value={form.adjustment}
              onChange={(event) => setForm((current) => ({ ...current, adjustment: event.target.value }))}
              inputProps={{ step: "0.01", min: "-99", max: "9900" }}
              helperText="0 mantiene el precio base; 15 agrega 15%; -10 aplica 10% de descuento."
              error={form.adjustment !== "" && (!Number.isFinite(adjustment) || coefficient < 0.01 || coefficient > 100)}
            />
            <Grid container spacing={1.3}>
              <Grid item xs={12} sm={7}>
                <TextField
                  select
                  fullWidth
                  label="Redondeo comercial"
                  value={form.roundingPreset}
                  onChange={(event) => setForm((current) => ({ ...current, roundingPreset: event.target.value }))}
                >
                  {form.roundingPreset === "custom" ? (
                    <MenuItem value="custom">Configuración actual · {priceListRoundingLabel(priceList)}</MenuItem>
                  ) : null}
                  <MenuItem value="currency2">Centavos / 2 decimales</MenuItem>
                  <MenuItem value="integer">Número entero</MenuItem>
                  <MenuItem value="step10">Múltiplos de 10</MenuItem>
                  <MenuItem value="step100">Múltiplos de 100</MenuItem>
                  <MenuItem value="step1000">Múltiplos de 1.000</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  label="Dirección"
                  value={form.roundingDirection}
                  onChange={(event) => setForm((current) => ({ ...current, roundingDirection: event.target.value }))}
                >
                  <MenuItem value="nearest">Más cercano</MenuItem>
                  <MenuItem value="up">Hacia arriba</MenuItem>
                  <MenuItem value="down">Hacia abajo</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            {editing ? (
              <TextField
                select
                fullWidth
                label="Estado"
                value={form.status}
                onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
              >
                <MenuItem value="active">Activa y asignable</MenuItem>
                <MenuItem value="inactive">Inactiva</MenuItem>
              </TextField>
            ) : null}
            {editing && form.status === "inactive" && assignedCustomers > 0 ? (
              <Alert severity="warning">
                Hay {assignedCustomers} {assignedCustomers === 1 ? "cliente asignado" : "clientes asignados"}. Mientras la lista esté inactiva usarán la lista base; la asignación se conserva para que vuelva a aplicar si reactivás esta lista.
              </Alert>
            ) : null}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Vista previa</Typography>
              <Typography sx={{ mt: .3, fontWeight: 950, color: BRAND.dark }}>
                {formatMoney(1000, currency)} base → {preview === null ? "—" : formatMoney(preview, currency)}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
                El ajuste se aplica sobre el precio base del producto y luego se ejecuta el redondeo elegido.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={!valid || busy} startIcon={busy ? <WebProfitSpinner size={18} /> : editing ? <EditRoundedIcon /> : <AddCircleOutlineRoundedIcon />}>
            {editing ? "Guardar cambios" : "Crear lista"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function PriceListsManager({ priceLists, canManage, canAssign, currency, busy, onCreate, onEdit }) {
  const customLists = priceLists.filter((list) => !list.isBase);
  return (
    <Paper variant="outlined" sx={{ p: 1.7, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ md: "center" }}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Listas de precios del negocio</Typography>
          <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
            Creá reglas comerciales sobre el precio base y después asignalas a los clientes desde la tabla de abajo.
          </Typography>
        </Box>
        {canManage ? (
          <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />} onClick={onCreate} disabled={busy} sx={{ flexShrink: 0 }}>
            Nueva lista
          </Button>
        ) : null}
      </Stack>

      <Grid container spacing={1.2} sx={{ mt: .5 }}>
        {priceLists.map((list) => {
          const assigned = Number(list.assignedCustomers || 0);
          return (
            <Grid item xs={12} md={6} lg={4} key={list.id}>
              <Paper variant="outlined" sx={{ p: 1.5, height: "100%", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{list.name}</Typography>
                    <Typography variant="body2" sx={{ mt: .2, fontWeight: 800, color: list.isBase ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.7)" : BRAND.orange }}>
                      {priceListAdjustmentLabel(list)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={list.active ? "success" : "warning"}
                    variant={list.active ? "outlined" : "filled"}
                    label={list.isBase ? "Base" : list.active ? "Activa" : "Inactiva"}
                    sx={{ fontWeight: 850 }}
                  />
                </Stack>
                <Stack spacing={.25} sx={{ mt: 1.1 }}>
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
                    {priceListRoundingLabel(list)} · {String(list.currency || currency).toUpperCase()}
                  </Typography>
                  {!list.isBase ? (
                    <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
                      {assigned} {assigned === 1 ? "cliente asignado" : "clientes asignados"}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
                      Referencia del catálogo. No se edita ni se elimina.
                    </Typography>
                  )}
                </Stack>
                {!list.isBase && canManage ? (
                  <Button size="small" variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => onEdit(list)} disabled={busy} sx={{ mt: 1.2 }}>
                    Editar lista
                  </Button>
                ) : null}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {customLists.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1.3 }}>
          Todavía no hay listas personalizadas. Creá la primera y después asignala al cliente que corresponda.
        </Alert>
      ) : null}
      {!canManage ? (
        <Alert severity="info" sx={{ mt: 1.3 }}>
          Podés ver las listas, pero necesitás el permiso “Administrar listas de precios” para crearlas o editarlas.
        </Alert>
      ) : null}
      {!canAssign ? (
        <Alert severity="info" sx={{ mt: 1.3 }}>
          Las asignaciones están en modo lectura: necesitás el permiso “Asignar listas de precios” para cambiar la lista de un cliente.
        </Alert>
      ) : null}
    </Paper>
  );
}

function CustomerMobileCard({
  customer,
  priceLists,
  canManageStatus,
  canManagePricing,
  pricingEnabled,
  membershipsEnabled,
  canManageMemberships,
  busy,
  onOpen,
  onPriceList,
  onStatus,
  onRenew,
  onAssign,
}) {
  const status = statusMeta(customer.status);
  return (
    <Card variant="outlined" sx={{ borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
      <CardContent>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar sx={{ bgcolor: BRAND.orange, fontWeight: 950 }}>{customerInitials(customer)}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>
              {customer.email || "Sin email"}
            </Typography>
          </Box>
          <Stack spacing={.6} alignItems="flex-end">
            <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 850 }} />
            {customer.origin === "manual" ? <Chip size="small" variant="outlined" label="Carga manual" /> : null}
          </Stack>
        </Stack>

        <Grid container spacing={1.2} sx={{ mt: .4 }}>
          <Grid item xs={6}><DetailValue label="Alta">{formatDate(customer.createdAt)}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Último acceso">{formatDate(customer.lastLoginAt, true)}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Pedidos">{customer.activity?.orderCount || 0}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Actividad">{formatDate(customer.lastActivityAt, true)}</DetailValue></Grid>
        </Grid>

        {membershipsEnabled ? (
          <Box sx={{ mt: 1.2 }}>
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>Membresía</Typography>
            <MembershipStatus customer={customer} canManage={canManageMemberships} onRenew={onRenew} onAssign={onAssign} compact />
          </Box>
        ) : null}

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {pricingEnabled ? (
            <PriceListSelect
              customer={customer}
              priceLists={priceLists}
              disabled={!canManagePricing || busy}
              onChange={(value) => onPriceList(customer, value)}
            />
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="outlined" onClick={() => onOpen(customer)} startIcon={<OpenInNewRoundedIcon />}>
              Ver ficha
            </Button>
            {customer.status !== "deleted" && canManageStatus ? (
              <Button
                fullWidth
                color={customer.status === "suspended" ? "success" : "error"}
                variant="outlined"
                disabled={busy}
                onClick={() => onStatus(customer)}
              >
                {customer.status === "suspended" ? "Reactivar" : "Suspender"}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CustomerDetailDialog({
  open,
  loading,
  detail,
  priceLists,
  projectId,
  projectTimezone,
  membershipsEnabled,
  canManageMemberships,
  canCollectMembershipPayments,
  canEditManualProfile,
  onEditProfile,
  renewMembershipId,
  collectMembershipId,
  onMembershipChanged,
  canManageStatus,
  canManagePricing,
  pricingEnabled,
  busy,
  onClose,
  onPriceList,
  onStatus,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const customer = detail?.customer;
  const status = statusMeta(customer?.status);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 7 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: BRAND.orange, fontWeight: 950 }}>{customerInitials(customer)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {customer?.name || "Ficha del cliente"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>
              {customer?.email || customer?.phone || customer?.document || "Sin email"}
            </Typography>
          </Box>
          {customer ? <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 850 }} /> : null}
        </Stack>
        <IconButton aria-label="Cerrar ficha" onClick={onClose} disabled={busy} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <DashboardLoadingState label="Cargando ficha del cliente…" minHeight={260} />
        ) : !customer ? (
          <Alert severity="error">No se pudo cargar la ficha del cliente.</Alert>
        ) : (
          <Stack spacing={2.2}>
            <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ mb: 1.4 }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Contacto y registro</Typography>
                {canEditManualProfile ? (
                  <Button size="small" variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => onEditProfile(customer)}>
                    {customer.email && customer.phone && customer.document ? "Editar datos" : "Completar datos"}
                  </Button>
                ) : null}
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><DetailValue label="Email">{customer.email || "Sin email"}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Teléfono">{customer.phone || "Sin teléfono"}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Documento">{customer.document || "Sin documento"}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Fecha de registro">{formatDate(customer.createdAt, true)}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Último acceso">{formatDate(customer.lastLoginAt, true)}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Última actividad">{formatDate(customer.lastActivityAt, true)}</DetailValue></Grid>
              </Grid>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {customer.webAccess?.enabled === true ? (
                  <Chip size="small" variant="outlined" label={customer.origin === "manual" ? "Carga manual · acceso web vinculado" : "Cliente con acceso web"} />
                ) : (
                  <Chip size="small" variant="outlined" label="Carga manual · sin acceso web" />
                )}
                <Chip
                  size="small"
                  icon={<MailOutlineRoundedIcon />}
                  label={customer.marketing?.acceptsEmail ? "Acepta emails" : "No acepta emails"}
                  color={customer.marketing?.acceptsEmail ? "success" : "default"}
                />
                <Chip
                  size="small"
                  icon={<WhatsAppIcon />}
                  label={customer.marketing?.acceptsWhatsapp ? "Acepta WhatsApp" : "No acepta WhatsApp"}
                  color={customer.marketing?.acceptsWhatsapp ? "success" : "default"}
                />
              </Stack>
              {membershipsEnabled ? (
                <Alert severity={customer.email ? "info" : "warning"} sx={{ mt: 1.5 }}>
                  {customer.email
                    ? customer.webAccess?.enabled === true
                      ? `Autogestión activa: ${customer.email} ya está vinculado a una cuenta web. Sus mensualidades asignadas aparecen en “Mis mensualidades” y las cuotas pendientes pueden pagarse con Mercado Pago cuando el medio esté conectado.`
                      : `Autogestión disponible: el cliente puede registrarse en la web con ${customer.email}. WebProfit verificará ese correo antes de vincular esta ficha y conservará sus membresías e historial.`
                    : "Para que este cliente pueda autogestionar y pagar sus mensualidades desde la web, agregale un email a la ficha. No hace falta crearle una contraseña desde el gimnasio."}
                  {!customer.email && canEditManualProfile ? (
                    <Button size="small" sx={{ ml: 1 }} onClick={() => onEditProfile(customer)}>Agregar email</Button>
                  ) : null}
                </Alert>
              ) : null}
            </Paper>

            <CustomerMembershipsCard
              projectId={projectId}
              customerId={customer.id}
              enabled={membershipsEnabled}
              canManage={canManageMemberships}
              canCollect={canCollectMembershipPayments}
              renewMembershipId={renewMembershipId}
              collectMembershipId={collectMembershipId}
              timezone={projectTimezone}
              onMembershipChanged={onMembershipChanged}
            />

            {pricingEnabled ? (
              <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Lista de precios personal</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                      El cambio se aplica en la próxima consulta de precios del cliente y vuelve a validarse al comprar.
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: { sm: 280 } }}>
                    <PriceListSelect
                      customer={customer}
                      priceLists={priceLists}
                      disabled={!canManagePricing || busy}
                      onChange={(value) => onPriceList(customer, value)}
                    />
                  </Box>
                </Stack>
                {customer.priceList && !customer.priceList.active ? (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    La lista asignada está inactiva o ya no está disponible. El cliente puede volver a la lista base o recibir otra lista activa.
                  </Alert>
                ) : null}
                {!canManagePricing ? <Alert severity="info" sx={{ mt: 1.5 }}>Tenés acceso de lectura, pero no permiso para modificar listas de precios.</Alert> : null}
              </Paper>
            ) : null}

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Actividad comercial</Typography>
                  <Grid container spacing={1.2} sx={{ mt: .2 }}>
                    <Grid item xs={6}><DetailValue label="Pedidos">{customer.activity?.orderCount || 0}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Valor registrado">{formatMoney(customer.activity?.orderValue, customer.activity?.orderCurrency)}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Turnos">{customer.activity?.appointmentCount || 0}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Último pedido">{formatDate(customer.activity?.lastOrderAt, true)}</DetailValue></Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Estado del cliente</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Suspender conserva pedidos, turnos, precios e historial.</Typography>
                    </Box>
                    {canManageStatus && customer.status !== "deleted" ? (
                      <Button
                        variant="outlined"
                        color={customer.status === "suspended" ? "success" : "error"}
                        disabled={busy}
                        startIcon={customer.status === "suspended" ? <CheckCircleRoundedIcon /> : <BlockRoundedIcon />}
                        onClick={() => onStatus(customer)}
                      >
                        {customer.status === "suspended" ? "Reactivar" : "Suspender"}
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {Array.isArray(customer.addresses) && customer.addresses.length ? (
              <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Direcciones guardadas</Typography>
                <Stack spacing={1}>
                  {customer.addresses.map((address, index) => (
                    <Box key={address.id || address._id || index} sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}>
                      <Typography sx={{ fontWeight: 850 }}>{address.label || `Dirección ${index + 1}`}</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                        {[
                          address.street,
                          address.number,
                          address.floor ? `Piso ${address.floor}` : "",
                          address.apartment ? `Depto ${address.apartment}` : "",
                          address.city,
                          address.province,
                          address.postalCode,
                        ].filter(Boolean).join(", ") || "Sin detalle"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Pedidos recientes</Typography>
              {!detail.recentOrders?.length ? (
                <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Este cliente todavía no tiene pedidos registrados.</Typography>
              ) : (
                <Stack divider={<Divider flexItem />}>
                  {detail.recentOrders.map((order) => (
                    <Stack key={order.id} direction="row" justifyContent="space-between" spacing={1.5} sx={{ py: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 850 }}>{order.orderNumber || order.id}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{formatDate(order.createdAt, true)} · {order.status}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 900 }}>{formatMoney(order.total, order.currency)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>

            {((pricingEnabled && customer.priceListHistory?.length) || customer.statusHistory?.length) ? (
              <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Auditoría de cambios</Typography>
                <Stack spacing={1}>
                  {[...(pricingEnabled ? (customer.priceListHistory || []).map((item) => ({ ...item, kind: "price" })) : []), ...(customer.statusHistory || []).map((item) => ({ ...item, kind: "status" }))]
                    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
                    .slice(0, 20)
                    .map((item, index) => (
                      <Box key={item.id || item._id || `${item.kind}-${index}`} sx={{ p: 1.1, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {item.kind === "price"
                            ? `${item.fromPriceListName || "Lista base"} → ${item.toPriceListName || "Lista base"}`
                            : `${item.fromStatus || "—"} → ${item.toStatus || "—"}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                          {formatDate(item.at, true)} · {item.actor?.name || item.actor?.email || "Sistema"}{item.reason ? ` · ${item.reason}` : ""}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

const EMPTY_PRIMARY_ADDRESS = Object.freeze({
  label: "Principal",
  street: "",
  number: "",
  floor: "",
  apartment: "",
  city: "",
  province: "",
  postalCode: "",
});

function customerPrimaryAddress(customer) {
  const addresses = Array.isArray(customer?.addresses) ? customer.addresses : [];
  return addresses.find((address) => address?.isDefault === true) || addresses[0] || null;
}

function manualCustomerForm(customer = null) {
  const primary = customerPrimaryAddress(customer);
  return {
    name: customer?.name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    document: customer?.document || "",
    primaryAddress: {
      ...EMPTY_PRIMARY_ADDRESS,
      label: primary?.label || "Principal",
      street: primary?.street || "",
      number: primary?.number || "",
      floor: primary?.floor || "",
      apartment: primary?.apartment || "",
      city: primary?.city || "",
      province: primary?.province || "",
      postalCode: primary?.postalCode || "",
    },
    marketing: {
      acceptsEmail: customer?.marketing?.acceptsEmail === true,
      acceptsWhatsapp: customer?.marketing?.acceptsWhatsapp === true,
    },
  };
}

function hasAdditionalCustomerData(customer) {
  const primary = customerPrimaryAddress(customer);
  return Boolean(
    primary
    || customer?.marketing?.acceptsEmail === true
    || customer?.marketing?.acceptsWhatsapp === true,
  );
}

function manualCustomerPayload(form) {
  return {
    name: form.name,
    email: form.email,
    phone: form.phone,
    document: form.document,
    primaryAddress: form.primaryAddress,
    marketing: form.marketing,
  };
}

function AdditionalCustomerFields({ form, setForm, disabled = false }) {
  const changeAddress = (field) => (event) => setForm((current) => ({
    ...current,
    primaryAddress: { ...current.primaryAddress, [field]: event.target.value },
  }));
  const changeMarketing = (field) => (event) => setForm((current) => ({
    ...current,
    marketing: { ...current.marketing, [field]: event.target.checked },
  }));

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)", bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)" }}>
      <Stack spacing={1.35}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Dirección principal</Typography>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
            Se actualiza la dirección principal sin borrar otras direcciones guardadas en la ficha.
          </Typography>
        </Box>
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Etiqueta" value={form.primaryAddress.label} onChange={changeAddress("label")} disabled={disabled} inputProps={{ maxLength: 80 }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth size="small" label="Calle" value={form.primaryAddress.street} onChange={changeAddress("street")} disabled={disabled} inputProps={{ maxLength: 160 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Número" value={form.primaryAddress.number} onChange={changeAddress("number")} disabled={disabled} inputProps={{ maxLength: 40 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Piso" value={form.primaryAddress.floor} onChange={changeAddress("floor")} disabled={disabled} inputProps={{ maxLength: 40 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Departamento" value={form.primaryAddress.apartment} onChange={changeAddress("apartment")} disabled={disabled} inputProps={{ maxLength: 40 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Código postal" value={form.primaryAddress.postalCode} onChange={changeAddress("postalCode")} disabled={disabled} inputProps={{ maxLength: 40 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Ciudad" value={form.primaryAddress.city} onChange={changeAddress("city")} disabled={disabled} inputProps={{ maxLength: 120 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Provincia" value={form.primaryAddress.province} onChange={changeAddress("province")} disabled={disabled} inputProps={{ maxLength: 120 }} />
          </Grid>
        </Grid>
        <Divider />
        <Box>
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Preferencias de contacto</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={form.marketing.acceptsEmail} onChange={changeMarketing("acceptsEmail")} disabled={disabled} />}
              label="Acepta email"
            />
            <FormControlLabel
              control={<Checkbox checked={form.marketing.acceptsWhatsapp} onChange={changeMarketing("acceptsWhatsapp")} disabled={disabled} />}
              label="Acepta WhatsApp"
            />
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function ManualCustomerDialog({
  open,
  busy,
  error,
  duplicate,
  allowMinimalIdentity = true,
  onClose,
  onSubmit,
  onReviewDuplicate,
  onClearDuplicate,
}) {
  const [form, setForm] = React.useState(() => manualCustomerForm());
  const [showAdditional, setShowAdditional] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(manualCustomerForm());
      setShowAdditional(false);
    }
  }, [open]);

  const change = (field) => (event) => {
    onClearDuplicate?.();
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };
  const hasUsablePhone = form.phone.replace(/\D+/g, "").length >= 6;
  const hasUsableDocument = form.document.replace(/[.\s-]+/g, "").length >= 3;
  const hasIdentity = form.email.trim() || hasUsablePhone || hasUsableDocument;
  const valid = form.name.trim() && (allowMinimalIdentity ? hasIdentity : form.email.trim());

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={(event) => { event.preventDefault(); if (valid && !busy) onSubmit(manualCustomerPayload(form)); }}>
        <DialogTitle sx={{ fontWeight: 950 }}>Agregar cliente</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            <Alert severity="info">
              Para una carga rápida alcanza con nombre y un email, teléfono o documento. Se crea una ficha CRM sin contraseña; los datos operativos adicionales son opcionales.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {duplicate ? (
              <Alert severity={duplicate.code === "CUSTOMER_POSSIBLE_DUPLICATE" ? "warning" : "error"}>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {duplicate.message || "Encontramos una ficha existente con datos coincidentes."}
                  </Typography>
                  {Array.isArray(duplicate.match?.matchedFields) && duplicate.match.matchedFields.length ? (
                    <Typography variant="caption">
                      Coincidencias: {duplicate.match.matchedFields.map((field) => ({ email: "email", document: "documento", phone: "teléfono", name: "nombre" }[field] || field)).join(", ")}.
                    </Typography>
                  ) : null}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    {duplicate.customerId ? (
                      <Button size="small" variant="outlined" onClick={onReviewDuplicate} disabled={busy}>
                        Abrir ficha existente
                      </Button>
                    ) : null}
                    {duplicate.canCreateSeparate ? (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onSubmit({ ...manualCustomerPayload(form), duplicateResolution: "create_separate" })}
                        disabled={!valid || busy}
                      >
                        Crear como persona distinta
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Alert>
            ) : null}
            <TextField autoFocus required fullWidth label="Nombre completo" value={form.name} onChange={change("name")} inputProps={{ maxLength: 180 }} />
            <TextField
              required={!allowMinimalIdentity}
              fullWidth
              type="email"
              label={allowMinimalIdentity ? "Email (opcional)" : "Email"}
              value={form.email}
              onChange={change("email")}
              helperText={allowMinimalIdentity ? "Si no hay email, completá teléfono o documento para identificar la ficha." : ""}
              inputProps={{ maxLength: 220 }}
            />
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono / WhatsApp" value={form.phone} onChange={change("phone")} inputProps={{ maxLength: 80 }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Documento" value={form.document} onChange={change("document")} inputProps={{ maxLength: 100 }} /></Grid>
            </Grid>
            <Button variant="text" onClick={() => setShowAdditional((value) => !value)} sx={{ alignSelf: "flex-start", px: .5 }}>
              {showAdditional ? "Ocultar datos adicionales" : "Agregar datos adicionales"}
            </Button>
            {showAdditional ? <AdditionalCustomerFields form={form} setForm={setForm} disabled={busy} /> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={!valid || busy} startIcon={busy ? <WebProfitSpinner size={18} /> : <PersonAddAlt1RoundedIcon />}>
            Crear cliente
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function ManualCustomerProfileDialog({ open, customer, busy, error, onClose, onSubmit }) {
  const [form, setForm] = React.useState(() => manualCustomerForm());
  const [showAdditional, setShowAdditional] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setForm(manualCustomerForm(customer));
    setShowAdditional(hasAdditionalCustomerData(customer));
  }, [customer, open]);

  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const hasUsablePhone = form.phone.replace(/\D+/g, "").length >= 6;
  const hasUsableDocument = form.document.replace(/[.\s-]+/g, "").length >= 3;
  const valid = form.name.trim() && (form.email.trim() || hasUsablePhone || hasUsableDocument);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={(event) => { event.preventDefault(); if (valid && !busy) onSubmit(manualCustomerPayload(form)); }}>
        <DialogTitle sx={{ fontWeight: 950 }}>Completar datos del cliente</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            <Alert severity="info">
              Este cambio actualiza la ficha manual del CRM. No crea credenciales ni acceso web y conserva otras direcciones e historial que no estés editando.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField autoFocus required fullWidth label="Nombre completo" value={form.name} onChange={change("name")} inputProps={{ maxLength: 180 }} />
            <TextField fullWidth type="email" label="Email (opcional)" value={form.email} onChange={change("email")} inputProps={{ maxLength: 220 }} />
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono / WhatsApp" value={form.phone} onChange={change("phone")} inputProps={{ maxLength: 80 }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Documento" value={form.document} onChange={change("document")} inputProps={{ maxLength: 100 }} /></Grid>
            </Grid>
            <Button variant="text" onClick={() => setShowAdditional((value) => !value)} sx={{ alignSelf: "flex-start", px: .5 }}>
              {showAdditional ? "Ocultar datos adicionales" : "Editar datos adicionales"}
            </Button>
            {showAdditional ? <AdditionalCustomerFields form={form} setForm={setForm} disabled={busy} /> : null}
            {!valid ? (
              <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 750 }}>
                Conservá al menos un email, teléfono o documento además del nombre.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={!valid || busy} startIcon={busy ? <WebProfitSpinner size={18} /> : <EditRoundedIcon />}>
            Guardar datos
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default function ProjectCustomersPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { activeProject, projects, setActiveProject, applyProjectUpdate } = useProjects();
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [accessLoading, setAccessLoading] = React.useState(true);
  const [customers, setCustomers] = React.useState([]);
  const [priceLists, setPriceLists] = React.useState([{ id: "base", name: "Lista base", active: true, status: "active", isBase: true }]);
  const [summary, setSummary] = React.useState(EMPTY_SUMMARY);
  const [pagination, setPagination] = React.useState({ page: 1, pageSize: 25, total: 0, pages: 1 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [preference, setPreference] = React.useState("all");
  const [priceListFilter, setPriceListFilter] = React.useState("all");
  const [membershipStatus, setMembershipStatus] = React.useState("all");
  const [membershipPaymentStatus, setMembershipPaymentStatus] = React.useState("all");
  const [membershipSummary, setMembershipSummary] = React.useState(EMPTY_MEMBERSHIP_SUMMARY);
  const [membershipPaymentSummary, setMembershipPaymentSummary] = React.useState(EMPTY_MEMBERSHIP_PAYMENT_SUMMARY);
  const [membershipUsageSummary, setMembershipUsageSummary] = React.useState(EMPTY_MEMBERSHIP_USAGE_SUMMARY);
  const [membershipUsageStatus, setMembershipUsageStatus] = React.useState("all");
  const [membershipItems, setMembershipItems] = React.useState([]);
  const [membershipItemsLoading, setMembershipItemsLoading] = React.useState(false);
  const [membershipWorkspaceRows, setMembershipWorkspaceRows] = React.useState([]);
  const [membershipWorkspacePagination, setMembershipWorkspacePagination] = React.useState({ page: 1, pageSize: 25, total: 0, pages: 1 });
  const [membershipWorkspaceLoading, setMembershipWorkspaceLoading] = React.useState(false);
  const [membershipPlanOpen, setMembershipPlanOpen] = React.useState(false);
  const [membershipPlanBusy, setMembershipPlanBusy] = React.useState(false);
  const [membershipPlanError, setMembershipPlanError] = React.useState("");
  const [membershipPlanForm, setMembershipPlanForm] = React.useState({ name: "", price: "", periodValue: "1", periodUnit: "months", usageEnabled: false, includedUses: "", usageLabel: "usos" });
  const [workspaceUsageAction, setWorkspaceUsageAction] = React.useState(null);
  const [workspaceUsageBusy, setWorkspaceUsageBusy] = React.useState(false);
  const [membershipExpiringDays, setMembershipExpiringDays] = React.useState(7);
  const [membershipReminders, setMembershipReminders] = React.useState(EMPTY_MEMBERSHIP_REMINDERS);
  const [membershipRemindersLoading, setMembershipRemindersLoading] = React.useState(false);
  const [renewMembershipId, setRenewMembershipId] = React.useState("");
  const [collectMembershipId, setCollectMembershipId] = React.useState("");
  const [membershipFocus, setMembershipFocus] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [busyCustomerId, setBusyCustomerId] = React.useState("");
  const [statusPrompt, setStatusPrompt] = React.useState(null);
  const [statusReason, setStatusReason] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createBusy, setCreateBusy] = React.useState(false);
  const [createError, setCreateError] = React.useState("");
  const [createDuplicate, setCreateDuplicate] = React.useState(null);
  const [profileEditOpen, setProfileEditOpen] = React.useState(false);
  const [profileEditBusy, setProfileEditBusy] = React.useState(false);
  const [profileEditError, setProfileEditError] = React.useState("");
  const [priceListDialog, setPriceListDialog] = React.useState(null);
  const [priceListBusy, setPriceListBusy] = React.useState(false);
  const [priceListError, setPriceListError] = React.useState("");
  const [membershipActivationBusy, setMembershipActivationBusy] = React.useState(false);
  const requestRef = React.useRef(0);
  const quickActionHandledRef = React.useRef("");
  const deepLinkHandledRef = React.useRef("");
  const searchInputRef = React.useRef(null);

  const canRead = hasProjectPermission(memberAccess, "customers.read");
  const canManageStatus = hasProjectPermission(memberAccess, "customers.manage");
  const canCreateCustomer = hasProjectPermission(memberAccess, "customers.manage");
  const canCreateItems = hasProjectPermission(memberAccess, "items.create");
  const canReadPriceLists = hasProjectPermission(memberAccess, "price_lists.read");
  const canManagePriceLists = canReadPriceLists && hasProjectPermission(memberAccess, "price_lists.manage");
  const canManagePricing = canReadPriceLists && hasProjectPermission(memberAccess, "customers.pricing.manage");
  const effectiveProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects],
  );
  const projectCurrency = String(effectiveProject?.commerce?.currency || effectiveProject?.currency || "ARS").toUpperCase();
  const membershipsEnabled = isProjectCapabilityEnabled(effectiveProject, "memberships");
  const canManageProjectSettings = hasProjectPermission(memberAccess, "project.settings.manage");
  const canManageMemberships = membershipsEnabled && hasProjectPermission(memberAccess, "memberships.manage");
  const canCollectMembershipPayments = membershipsEnabled && hasProjectPermission(memberAccess, "memberships.payments.create");
  const requestedAction = String(searchParams.get("accion") || "").trim().toLowerCase();
  const legacyActionView = ["asignar-plan", "renovar"].includes(requestedAction)
    ? "membresias"
    : requestedAction === "vencimientos"
      ? "vencimientos"
      : "";
  const requestedView = String(searchParams.get("vista") || legacyActionView || "crm").trim().toLowerCase();
  const membershipCapabilityRequested = requestedView === "membresias" || requestedView === "vencimientos";
  const membershipCapabilityUnavailable = membershipCapabilityRequested && !membershipsEnabled;
  const customerView = requestedView === "precios" && canReadPriceLists
    ? "precios"
    : requestedView === "membresias"
      ? "membresias"
      : requestedView === "vencimientos"
        ? "vencimientos"
        : "crm";
  const membershipView = customerView === "membresias";
  const expirationView = customerView === "vencimientos";
  const pricingView = customerView === "precios";
  const showMembershipWorkspace = membershipsEnabled && (membershipView || expirationView);
  const viewTitle = pricingView
    ? "Listas de precios"
    : membershipView
      ? "Membresías"
      : expirationView
        ? "Vencimientos de membresías"
        : "CRM de clientes";
  const viewDescription = pricingView
    ? `Creación y asignación de listas de precios para clientes de ${effectiveProject?.name || "este negocio"}.`
    : membershipView
      ? `Servicios recurrentes, vigencias y renovaciones de clientes de ${effectiveProject?.name || "este negocio"}.`
      : expirationView
        ? `Seguimiento de próximas a vencer y vencidas de ${effectiveProject?.name || "este negocio"}.`
        : `Clientes de ${effectiveProject?.name || "este negocio"}, cargados desde la web o manualmente.`;

  React.useEffect(() => {
    if (!legacyActionView || searchParams.get("vista")) return;
    const next = new URLSearchParams(searchParams);
    next.set("vista", legacyActionView);
    setSearchParams(next, { replace: true });
  }, [legacyActionView, searchParams, setSearchParams]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    let active = true;
    setAccessLoading(true);
    setError("");
    setCustomers([]);
    setDetail(null);
    setCreateOpen(false);
    setCreateError("");
    setCreateDuplicate(null);
    setProfileEditOpen(false);
    setProfileEditError("");
    setPriceListDialog(null);
    setPriceListBusy(false);
    setPriceListError("");
    setMembershipStatus("all");
    setMembershipPaymentStatus("all");
    setMembershipUsageStatus("all");
    setMembershipSummary(EMPTY_MEMBERSHIP_SUMMARY);
    setMembershipPaymentSummary(EMPTY_MEMBERSHIP_PAYMENT_SUMMARY);
    setMembershipUsageSummary(EMPTY_MEMBERSHIP_USAGE_SUMMARY);
    setMembershipItems([]);
    setMembershipItemsLoading(false);
    setMembershipWorkspaceRows([]);
    setMembershipWorkspacePagination({ page: 1, pageSize: 25, total: 0, pages: 1 });
    setMembershipWorkspaceLoading(false);
    setMembershipPlanOpen(false);
    setMembershipPlanError("");
    setWorkspaceUsageAction(null);
    setMembershipExpiringDays(7);
    setMembershipReminders(EMPTY_MEMBERSHIP_REMINDERS);
    setMembershipRemindersLoading(false);
    setRenewMembershipId("");
    setCollectMembershipId("");
    setMembershipFocus("");
    quickActionHandledRef.current = "";
    deepLinkHandledRef.current = "";
    setPage(0);

    setPriceLists([{ id: "base", name: "Lista base", active: true, status: "active", isBase: true }]);

    (async () => {
      try {
        const projectData = await projectsApi.get(projectId);
        if (!active) return;
        const resolvedAccess = normalizeProjectAccess(projectData.memberAccess || projectData.member || {});
        setMemberAccess(resolvedAccess);
        if (projectData.project) setActiveProject(projectData.project);

        if (hasProjectPermission(resolvedAccess, "customers.read") && hasProjectPermission(resolvedAccess, "price_lists.read")) {
          const priceListData = await customersApi.priceLists(projectId);
          if (!active) return;
          setPriceLists(priceListData.priceLists?.length
            ? priceListData.priceLists
            : [{ id: "base", name: "Lista base", active: true, status: "active", isBase: true }]);
        }
      } catch (err) {
        if (!active) return;
        setError(err.message || "No se pudo abrir el CRM de este negocio.");
      } finally {
        if (active) setAccessLoading(false);
      }
    })();

    return () => { active = false; };
  }, [projectId, setActiveProject]);

  const loadCustomers = React.useCallback(async () => {
    if (!projectId || !canRead) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await customersApi.list(projectId, {
        page: page + 1,
        pageSize,
        q: search,
        status: status === "all" ? "" : status,
        preference: preference === "all" ? "" : preference,
        priceListId: canReadPriceLists && pricingView && priceListFilter !== "all" ? priceListFilter : "",
        membershipStatus: expirationView && membershipStatus !== "all" ? membershipStatus : "",
        membershipPaymentStatus: expirationView && membershipPaymentStatus !== "all" ? membershipPaymentStatus : "",
      });
      if (requestId !== requestRef.current) return;
      setCustomers(data.customers || []);
      setSummary(data.summary || EMPTY_SUMMARY);
      if (!membershipView) {
        setMembershipSummary(data.membershipSummary || EMPTY_MEMBERSHIP_SUMMARY);
        setMembershipPaymentSummary(data.membershipPaymentSummary || EMPTY_MEMBERSHIP_PAYMENT_SUMMARY);
        if (Number.isInteger(Number(data.membershipExpiringDays))) setMembershipExpiringDays(Number(data.membershipExpiringDays));
      }
      setPagination(data.pagination || { page: page + 1, pageSize, total: 0, pages: 1 });
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setCustomers([]);
      setError(err.message || "No se pudieron cargar los clientes.");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [canRead, canReadPriceLists, expirationView, membershipPaymentStatus, membershipStatus, membershipView, page, pageSize, preference, priceListFilter, pricingView, projectId, search, status]);

  React.useEffect(() => {
    if (!accessLoading && canRead && !membershipCapabilityUnavailable) loadCustomers();
  }, [accessLoading, canRead, loadCustomers, membershipCapabilityUnavailable]);

  const loadPriceLists = React.useCallback(async () => {
    if (!projectId || !canReadPriceLists) return null;
    const data = await customersApi.priceLists(projectId);
    setPriceLists(data.priceLists?.length
      ? data.priceLists
      : [{ id: "base", name: "Lista base", active: true, status: "active", isBase: true, currency: projectCurrency }]);
    return data;
  }, [canReadPriceLists, projectCurrency, projectId]);

  const loadMembershipItems = React.useCallback(async () => {
    if (!projectId || !canRead || !membershipsEnabled || !membershipView) {
      setMembershipItems([]);
      return;
    }
    setMembershipItemsLoading(true);
    try {
      const data = await customersApi.membershipItems(projectId);
      setMembershipItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setMembershipItems([]);
      setError((current) => current || err.message || "No se pudieron cargar los tipos de membresía.");
    } finally {
      setMembershipItemsLoading(false);
    }
  }, [canRead, membershipView, membershipsEnabled, projectId]);

  const loadMembershipWorkspace = React.useCallback(async () => {
    if (!projectId || !canRead || !membershipsEnabled || !membershipView) {
      setMembershipWorkspaceRows([]);
      setMembershipWorkspacePagination({ page: 1, pageSize, total: 0, pages: 1 });
      return;
    }
    setMembershipWorkspaceLoading(true);
    try {
      const data = await customersApi.membershipWorkspace(projectId, {
        page: page + 1,
        pageSize,
        q: search,
        status: membershipStatus === "all" ? "" : membershipStatus,
        paymentStatus: membershipPaymentStatus === "all" ? "" : membershipPaymentStatus,
        usageStatus: membershipUsageStatus === "all" ? "" : membershipUsageStatus,
      });
      setMembershipWorkspaceRows(Array.isArray(data.rows) ? data.rows : []);
      setMembershipWorkspacePagination(data.pagination || { page: page + 1, pageSize, total: 0, pages: 1 });
      setMembershipSummary({ ...EMPTY_MEMBERSHIP_SUMMARY, ...(data.summary || {}) });
      setMembershipPaymentSummary({ ...EMPTY_MEMBERSHIP_PAYMENT_SUMMARY, ...(data.paymentSummary || {}) });
      setMembershipUsageSummary({ ...EMPTY_MEMBERSHIP_USAGE_SUMMARY, ...(data.usageSummary || {}) });
      if (Number.isInteger(Number(data.membershipExpiringDays))) setMembershipExpiringDays(Number(data.membershipExpiringDays));
    } catch (err) {
      setMembershipWorkspaceRows([]);
      setError((current) => current || err.message || "No se pudo cargar la gestión de membresías.");
    } finally {
      setMembershipWorkspaceLoading(false);
    }
  }, [canRead, membershipPaymentStatus, membershipStatus, membershipUsageStatus, membershipView, membershipsEnabled, page, pageSize, projectId, search]);

  React.useEffect(() => {
    if (!accessLoading && canRead && membershipsEnabled && membershipView) {
      loadMembershipItems();
      loadMembershipWorkspace();
    }
    if (!membershipView) {
      setMembershipItems([]);
      setMembershipWorkspaceRows([]);
      setMembershipUsageSummary(EMPTY_MEMBERSHIP_USAGE_SUMMARY);
    }
  }, [accessLoading, canRead, loadMembershipItems, loadMembershipWorkspace, membershipView, membershipsEnabled]);

  const loadMembershipReminders = React.useCallback(async () => {
    if (!projectId || !canRead || !membershipsEnabled || !expirationView) {
      setMembershipReminders(EMPTY_MEMBERSHIP_REMINDERS);
      return;
    }
    setMembershipRemindersLoading(true);
    try {
      const data = await customersApi.membershipReminders(projectId, { limit: 60 });
      setMembershipReminders({
        items: data.items || [],
        summary: data.summary || EMPTY_MEMBERSHIP_REMINDERS.summary,
        communication: data.communication || EMPTY_MEMBERSHIP_REMINDERS.communication,
        expiringDays: Number.isInteger(Number(data.expiringDays)) ? Number(data.expiringDays) : membershipExpiringDays,
        internalNotice: data.internalNotice || null,
      });
    } catch (err) {
      setMembershipReminders(EMPTY_MEMBERSHIP_REMINDERS);
      setError((current) => current || err.message || "No se pudieron actualizar los recordatorios de membresías.");
    } finally {
      setMembershipRemindersLoading(false);
    }
  }, [canRead, expirationView, membershipExpiringDays, membershipsEnabled, projectId]);

  React.useEffect(() => {
    if (!accessLoading && canRead && membershipsEnabled && expirationView) loadMembershipReminders();
    if (!membershipsEnabled || !expirationView) setMembershipReminders(EMPTY_MEMBERSHIP_REMINDERS);
  }, [accessLoading, canRead, expirationView, membershipsEnabled, loadMembershipReminders]);

  const refreshCrm = React.useCallback(async () => {
    await Promise.all([
      loadCustomers(),
      membershipsEnabled && membershipView ? loadMembershipWorkspace() : Promise.resolve(),
      membershipsEnabled && membershipView ? loadMembershipItems() : Promise.resolve(),
      membershipsEnabled && expirationView ? loadMembershipReminders() : Promise.resolve(),
    ]);
  }, [expirationView, loadCustomers, loadMembershipItems, loadMembershipReminders, loadMembershipWorkspace, membershipView, membershipsEnabled]);

  const openCustomer = async (customer, options = {}) => {
    setRenewMembershipId(options.renewMembershipId || "");
    setCollectMembershipId(options.collectMembershipId || "");
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail({ customer });
    setError("");
    try {
      const data = await customersApi.get(projectId, customer.id);
      setDetail(data);
      if (canReadPriceLists && data.priceLists?.length) setPriceLists(data.priceLists);
    } catch (err) {
      setError(err.message || "No se pudo cargar la ficha del cliente.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  React.useEffect(() => {
    if (accessLoading || !canRead || membershipCapabilityUnavailable) return undefined;
    const requestedCustomerId = String(searchParams.get("customerId") || "").trim();
    if (!requestedCustomerId) return undefined;

    const token = `${projectId}:${requestedCustomerId}`;
    if (deepLinkHandledRef.current === token) return undefined;
    deepLinkHandledRef.current = token;

    let active = true;
    setDetailOpen(true);
    setDetailLoading(true);
    setRenewMembershipId("");
    setCollectMembershipId("");
    setError("");
    customersApi.get(projectId, requestedCustomerId)
      .then((data) => {
        if (!active) return;
        setDetail(data);
        if (canReadPriceLists && data.priceLists?.length) setPriceLists(data.priceLists);
      })
      .catch((err) => {
        if (!active) return;
        setDetail(null);
        setDetailOpen(false);
        setError(err.message || "No se pudo abrir la ficha vinculada a este movimiento.");
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => { active = false; };
  }, [accessLoading, canRead, canReadPriceLists, membershipCapabilityUnavailable, projectId, searchParams]);

  const activateMemberships = async () => {
    if (!canManageProjectSettings || membershipsEnabled || membershipActivationBusy) return;
    try {
      setMembershipActivationBusy(true);
      setError("");
      setSuccess("");
      const data = await projectsApi.updateBusinessConfiguration(projectId, {
        capabilities: { memberships: true },
      });
      if (!data.project || !isProjectCapabilityEnabled(data.project, "memberships")) {
        throw new Error("El proyecto no confirmó la activación de Membresías.");
      }
      applyProjectUpdate({
        ...data.project,
        memberAccess,
      });
      setSuccess("Membresías activada para este proyecto.");
    } catch (err) {
      setError(err.message || "No se pudo activar Membresías para este proyecto.");
    } finally {
      setMembershipActivationBusy(false);
    }
  };

  const openRenewal = (customer) => {
    if (!canManageMemberships || !customer?.membership?.membershipId || customer.membership.renewable === false) return;
    setMembershipFocus("renew");
    openCustomer(customer, { renewMembershipId: customer.membership.membershipId });
  };

  const openMembershipAssignment = (customer) => {
    if (!canManageMemberships || !customer?.id) return;
    setMembershipFocus("assign");
    openCustomer(customer);
  };

  const focusMembershipAssignment = React.useCallback(() => {
    if (!membershipsEnabled || !canManageMemberships) return;
    setMembershipFocus("assign");
    setMembershipStatus("all");
    setPage(0);
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [canManageMemberships, membershipsEnabled]);

  const focusMembershipRenewals = React.useCallback(() => {
    if (!membershipsEnabled || !canManageMemberships) return;
    setMembershipFocus("renew");
    setMembershipStatus("all");
    setPage(0);
  }, [canManageMemberships, membershipsEnabled]);

  const focusMembershipExpirations = React.useCallback(() => {
    if (!membershipsEnabled) return;
    setMembershipFocus("expirations");
    setMembershipStatus("all");
    setPage(0);
  }, [membershipsEnabled]);

  const eligibleMembershipItems = React.useMemo(
    () => membershipItems.filter((item) => item?.membershipEligible === true && item?.eligibleForNewAssignment !== false),
    [membershipItems],
  );

  const openCreateMembershipPlan = React.useCallback(() => {
    if (!canCreateItems || !canManageMemberships) return;
    setMembershipPlanError("");
    setMembershipPlanForm({ name: "", price: "", periodValue: "1", periodUnit: "months", usageEnabled: false, includedUses: "", usageLabel: "usos" });
    setMembershipPlanOpen(true);
  }, [canCreateItems, canManageMemberships]);

  const createMembershipPlan = async () => {
    if (!canCreateItems || !canManageMemberships || membershipPlanBusy) return;
    const name = String(membershipPlanForm.name || "").trim();
    const price = Number(membershipPlanForm.price);
    const periodValue = Number(membershipPlanForm.periodValue);
    const includedUses = Number(membershipPlanForm.includedUses);
    if (!name) { setMembershipPlanError("Ingresá un nombre para la membresía."); return; }
    if (!Number.isFinite(price) || price < 0) { setMembershipPlanError("Ingresá un precio válido."); return; }
    if (!Number.isInteger(periodValue) || periodValue < 1 || periodValue > 10000) {
      setMembershipPlanError("La periodicidad debe ser un número entero entre 1 y 10000.");
      return;
    }
    if (membershipPlanForm.usageEnabled && (!Number.isInteger(includedUses) || includedUses < 1 || includedUses > 1000000)) {
      setMembershipPlanError("La cantidad incluida debe ser un número entero entre 1 y 1000000.");
      return;
    }
    try {
      setMembershipPlanBusy(true);
      setMembershipPlanError("");
      setError("");
      setSuccess("");
      await productsApi.create(projectId, {
        itemType: "service",
        itemKind: "service",
        erpItemKind: "service",
        name,
        description: "",
        category: "Membresías",
        priceType: "fixed",
        price,
        operational: {
          sellable: true,
          purchasable: false,
          publicVisible: false,
          membershipEligible: true,
        },
        membershipPlan: {
          periodValue,
          periodUnit: membershipPlanForm.periodUnit || "months",
          referencePrice: price,
          usageEnabled: Boolean(membershipPlanForm.usageEnabled),
          includedUses: membershipPlanForm.usageEnabled ? includedUses : null,
          usageLabel: String(membershipPlanForm.usageLabel || "usos").trim() || "usos",
        },
      });
      setMembershipPlanOpen(false);
      setMembershipFocus("assign");
      setSuccess("Tipo de membresía creado. Ahora asignalo a un cliente para iniciar su primer período.");
      await Promise.all([loadMembershipItems(), loadMembershipWorkspace()]);
    } catch (err) {
      setMembershipPlanError(err.message || "No se pudo crear el tipo de membresía.");
    } finally {
      setMembershipPlanBusy(false);
    }
  };

  const openWorkspaceUsage = (row, action = "consume", movement = null) => {
    if (!canManageMemberships || !row?.customer?.id || !row?.id) return;
    setWorkspaceUsageAction({
      row,
      action,
      movement,
      quantity: action === "reverse" ? String(movement?.quantity || 1) : "1",
      note: "",
      operationKey: createOperationKey("membership-usage"),
    });
  };

  const submitWorkspaceUsage = async () => {
    const action = workspaceUsageAction;
    if (!action || workspaceUsageBusy || !canManageMemberships) return;
    const quantity = Number(action.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      setError("La cantidad de usos debe ser un número entero mayor a cero.");
      return;
    }
    try {
      setWorkspaceUsageBusy(true);
      setError("");
      setSuccess("");
      const data = await customersApi.recordMembershipUsage(
        projectId,
        action.row.customer.id,
        action.row.id,
        {
          action: action.action,
          quantity,
          note: String(action.note || "").trim(),
          operationKey: action.operationKey,
          ...(action.action === "reverse" ? { movementId: action.movement?.id } : {}),
        },
      );
      setWorkspaceUsageAction(null);
      setSuccess(data.message || (action.action === "reverse" ? "Consumo anulado correctamente." : "Consumo registrado correctamente."));
      await Promise.all([loadMembershipWorkspace(), refreshDetail(action.row.customer.id).catch(() => {})]);
    } catch (err) {
      setError(err.message || "No se pudo registrar el movimiento de uso.");
    } finally {
      setWorkspaceUsageBusy(false);
    }
  };

  React.useEffect(() => {
    if (accessLoading || !canRead) return;
    const action = requestedAction;
    if (!action) return;
    const token = `${projectId}:${action}`;
    if (quickActionHandledRef.current === token) return;

    if (action === "crear-cliente") {
      quickActionHandledRef.current = token;
      if (canCreateCustomer) {
        setCreateError("");
        setCreateDuplicate(null);
        setCreateOpen(true);
      }
      return;
    }
    if (!membershipsEnabled) return;
    quickActionHandledRef.current = token;
    if (action === "asignar-plan") {
      if (canManageMemberships) focusMembershipAssignment();
      return;
    }
    if (action === "renovar") {
      if (canManageMemberships) focusMembershipRenewals();
      return;
    }
    if (action === "vencimientos") focusMembershipExpirations();
  }, [accessLoading, canCreateCustomer, canManageMemberships, canRead, focusMembershipAssignment, focusMembershipExpirations, focusMembershipRenewals, membershipsEnabled, projectId, requestedAction]);

  const openMembershipReminder = (reminder) => {
    if (!reminder?.customer?.id) return;
    openCustomer(reminder.customer);
  };

  const renewMembershipReminder = (reminder) => {
    if (!canManageMemberships || !reminder?.customer?.id || !reminder?.membershipId) return;
    setMembershipFocus("renew");
    openCustomer(reminder.customer, { renewMembershipId: reminder.membershipId });
  };

  const refreshDetail = async (customerId) => {
    if (!detailOpen || detail?.customer?.id !== customerId) return;
    const data = await customersApi.get(projectId, customerId);
    setDetail(data);
    if (canReadPriceLists && data.priceLists?.length) setPriceLists(data.priceLists);
  };

  const createManualCustomer = async (payload) => {
    if (!canCreateCustomer) return;
    setCreateBusy(true);
    setCreateError("");
    setCreateDuplicate(null);
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.create(projectId, payload);
      setCreateOpen(false);
      setCreateDuplicate(null);
      setSuccess(data.message || "Cliente creado correctamente.");
      await loadCustomers();
      if (data.customer?.id) await openCustomer(data.customer);
    } catch (err) {
      if (["CUSTOMER_IDENTITY_EXISTS", "CUSTOMER_POSSIBLE_DUPLICATE"].includes(err?.code)) {
        setCreateDuplicate(err.data || { code: err.code, message: err.message });
        setCreateError("");
      } else {
        setCreateError(err.message || "No se pudo crear el cliente.");
      }
    } finally {
      setCreateBusy(false);
    }
  };

  const reviewDuplicateCustomer = async () => {
    const customerId = createDuplicate?.customerId;
    if (!customerId) return;
    setCreateOpen(false);
    setCreateDuplicate(null);
    await openCustomer({ id: customerId });
  };

  const openManualProfileEdit = (customer) => {
    if (!canManageStatus || customer?.origin !== "manual" || customer?.webAccess?.enabled !== false) return;
    setProfileEditError("");
    setProfileEditOpen(true);
  };

  const updateManualCustomerProfile = async (payload) => {
    const customer = detail?.customer;
    if (!canManageStatus || !customer?.id || customer.origin !== "manual" || customer.webAccess?.enabled !== false) return;
    setProfileEditBusy(true);
    setProfileEditError("");
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.updateProfile(projectId, customer.id, {
        ...payload,
        expectedUpdatedAt: customer.updatedAt,
      });
      setProfileEditOpen(false);
      setDetail((current) => current ? { ...current, customer: data.customer } : current);
      setSuccess(data.message || "Datos del cliente actualizados.");
      await loadCustomers();
    } catch (err) {
      setProfileEditError(err.message || "No se pudieron actualizar los datos del cliente.");
    } finally {
      setProfileEditBusy(false);
    }
  };

  const openCreatePriceList = () => {
    if (!canManagePriceLists) return;
    setPriceListError("");
    setPriceListDialog({ mode: "create", priceList: null });
  };

  const openEditPriceList = (priceList) => {
    if (!canManagePriceLists || !priceList?.id || priceList.isBase) return;
    setPriceListError("");
    setPriceListDialog({ mode: "edit", priceList });
  };

  const savePriceList = async (payload) => {
    if (!canManagePriceLists || !priceListDialog) return;
    setPriceListBusy(true);
    setPriceListError("");
    setError("");
    setSuccess("");
    try {
      const data = priceListDialog.mode === "edit"
        ? await customersApi.updatePriceList(projectId, priceListDialog.priceList.id, {
            ...payload,
            expectedRevision: priceListDialog.priceList.revision,
          })
        : await customersApi.createPriceList(projectId, payload);
      if (data.priceLists?.length) setPriceLists(data.priceLists);
      setPriceListDialog(null);
      setSuccess(data.message || (priceListDialog.mode === "edit" ? "Lista de precios actualizada." : "Lista de precios creada."));
      await Promise.all([
        loadCustomers(),
        detailOpen && detail?.customer?.id ? refreshDetail(detail.customer.id).catch(() => {}) : Promise.resolve(),
      ]);
    } catch (err) {
      if (err?.code === "PRICE_LIST_CHANGED" && priceListDialog?.mode === "edit") {
        const refreshed = await loadPriceLists().catch(() => null);
        const latest = refreshed?.priceLists?.find((list) => list.id === priceListDialog.priceList.id) || err?.data?.priceList || null;
        if (latest) setPriceListDialog({ mode: "edit", priceList: latest });
      }
      setPriceListError(err.message || "No se pudo guardar la lista de precios.");
    } finally {
      setPriceListBusy(false);
    }
  };

  const assignPriceList = async (customer, nextPriceListId) => {
    if (!canManagePricing || !customer?.id) return;
    setBusyCustomerId(customer.id);
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.assignPriceList(
        projectId,
        customer.id,
        nextPriceListId === "base" ? null : nextPriceListId,
        customer.updatedAt,
      );
      setSuccess(data.message || "Lista de precios actualizada.");
      await Promise.all([loadCustomers(), loadPriceLists(), refreshDetail(customer.id)]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar la lista de precios.");
      if (err?.status === 409) {
        await Promise.all([loadCustomers(), loadPriceLists().catch(() => {}), refreshDetail(customer.id).catch(() => {})]);
      }
    } finally {
      setBusyCustomerId("");
    }
  };

  const askStatusChange = (customer) => {
    setStatusReason("");
    setStatusPrompt({
      customer,
      nextStatus: customer.status === "suspended" ? "active" : "suspended",
    });
  };

  const confirmStatusChange = async () => {
    const customer = statusPrompt?.customer;
    if (!customer || !canManageStatus) return;
    setBusyCustomerId(customer.id);
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.updateStatus(
        projectId,
        customer.id,
        statusPrompt.nextStatus,
        statusReason,
        customer.updatedAt,
      );
      setSuccess(data.message || "Estado actualizado.");
      setStatusPrompt(null);
      await Promise.all([loadCustomers(), refreshDetail(customer.id)]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar el estado del cliente.");
      if (err?.status === 409) {
        setStatusPrompt(null);
        await Promise.all([loadCustomers(), refreshDetail(customer.id).catch(() => {})]);
      }
    } finally {
      setBusyCustomerId("");
    }
  };

  if (accessLoading) {
    return <DashboardLoadingState label="Preparando clientes y permisos…" minHeight={360} />;
  }

  if (!canRead) {
    return (
      <Alert severity="warning">
        No tenés permiso para ver los clientes registrados de este negocio. Solicitá el permiso <strong>customers.read</strong> a un administrador.
      </Alert>
    );
  }

  if (membershipCapabilityUnavailable) {
    return (
      <Stack spacing={2.2}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
            bgcolor: "rgba(255,255,255,.92)",
          }}
        >
          <Stack spacing={1.6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Membresías no está habilitada
              </Typography>
            </Stack>

            <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", maxWidth: 760 }}>
              Esta función se activa por proyecto. Al habilitarla vas a poder asignar planes o servicios recurrentes,
              registrar renovaciones, consultar estados, pagos y vencimientos sin perder el CRM actual.
            </Typography>

            <Alert severity="info">
              El CRM sigue intacto y no se perdió información. Membresías simplemente no está habilitada para este proyecto.
            </Alert>

            {!canManageProjectSettings ? (
              <Alert severity="warning">
                Pedile a un administrador del proyecto que habilite Membresías desde Configuración &gt; Capacidades.
              </Alert>
            ) : null}

            {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {canManageProjectSettings ? (
                <Button
                  variant="contained"
                  startIcon={membershipActivationBusy ? <WebProfitSpinner size={18} color="inherit" /> : <CardMembershipRoundedIcon />}
                  onClick={activateMemberships}
                  disabled={membershipActivationBusy}
                >
                  {membershipActivationBusy ? "Activando…" : "Activar Membresías"}
                </Button>
              ) : null}
              <Button
                variant="outlined"
                onClick={() => setSearchParams(new URLSearchParams({ vista: "crm" }))}
                disabled={membershipActivationBusy}
              >
                Volver al CRM
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {expirationView
              ? <NotificationsActiveRoundedIcon sx={{ color: BRAND.orange }} />
              : membershipView
                ? <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
                : pricingView
                  ? <ShoppingBagRoundedIcon sx={{ color: BRAND.orange }} />
                  : <GroupsRoundedIcon sx={{ color: BRAND.orange }} />}
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{viewTitle}</Typography>
            {!pricingView && !showMembershipWorkspace ? (
              <ContextualHelp
                compact
                title="Qué representa una ficha del CRM"
                text="Una ficha reúne la relación con una persona dentro de este negocio. Puede existir aunque el cliente todavía no tenga contraseña ni acceso a la web."
                bullets={[
                  "Activo o suspendido controla el estado del cliente sin borrar su historial.",
                  "Pedidos y datos de contacto se vinculan a la misma ficha.",
                  "Las funciones adicionales aparecen en Clientes sólo cuando la capacidad y tus permisos lo permiten.",
                ]}
              />
            ) : null}
          </Stack>
          <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: .5 }}>
            {viewDescription}
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          {canCreateCustomer ? (
            <Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => { setCreateError(""); setCreateDuplicate(null); setCreateOpen(true); }}>
              Agregar cliente
            </Button>
          ) : null}
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={refreshCrm} disabled={loading || membershipRemindersLoading}>
            Actualizar
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {success ? <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert> : null}

      {membershipView && canManageMemberships ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: "rgba(105,87,232,.16)", bgcolor: "rgba(105,87,232,.025)" }}>
          <Stack spacing={1.3}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={.75} alignItems="center">
                  <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Cobros recurrentes y membresías</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                  Un tipo define precio, frecuencia y cupo. Cada asignación crea el período del cliente; el cobro de cada período se registra en la operatoria existente.
                </Typography>
              </Box>
              <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                {canCreateItems ? (
                  <Button size="small" variant={eligibleMembershipItems.length ? "outlined" : "contained"} startIcon={<AddCircleOutlineRoundedIcon />} onClick={openCreateMembershipPlan}>
                    Crear tipo de membresía
                  </Button>
                ) : null}
                <Button size="small" variant={membershipFocus === "assign" ? "contained" : "outlined"} startIcon={<CardMembershipRoundedIcon />} onClick={focusMembershipAssignment} disabled={!eligibleMembershipItems.length}>
                  Asignar a un cliente
                </Button>
              </Stack>
            </Stack>

            {membershipItemsLoading ? (
              <Stack direction="row" spacing={1} alignItems="center"><WebProfitSpinner size={18} /><Typography variant="body2">Revisando los tipos de membresía…</Typography></Stack>
            ) : !eligibleMembershipItems.length ? (
              <Alert severity="info"
                action={canCreateItems ? <Button color="inherit" size="small" onClick={openCreateMembershipPlan}>Crear ahora</Button> : null}
              >
                <strong>Creá tu primera membresía.</strong> Definí un nombre, cuánto se cobra, cada cuánto se renueva y, si corresponde, cuántos accesos, clases, sesiones o consumos incluye.
                {!canCreateItems ? " Necesitás permiso items.create para crear el tipo; un administrador puede configurarlo desde Ítems." : ""}
              </Alert>
            ) : membershipWorkspacePagination.total === 0 || membershipFocus === "assign" ? (
              <Paper variant="outlined" sx={{ p: 1.4, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Siguiente paso: asignar a un cliente</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                      Al asignar elegís la fecha de inicio. El fin del período se calcula con la periodicidad del tipo y la renovación genera un período nuevo, con cupo nuevo si tiene límite de usos.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                    {eligibleMembershipItems.slice(0, 8).map((item) => (
                      <Chip
                        key={item.id}
                        size="small"
                        variant="outlined"
                        label={`${item.name} · ${formatMoney(item.membershipPlan?.referencePrice ?? item.price, "ARS")} / ${membershipPeriodLabel(item.membershipPlan?.periodValue, item.membershipPlan?.periodUnit)}`}
                      />
                    ))}
                  </Stack>
                  {customers.length ? (
                    <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                      {customers.slice(0, 8).map((customer) => (
                        <Button key={customer.id} size="small" variant="outlined" onClick={() => openMembershipAssignment(customer)}>
                          {customer.name || customer.email || "Cliente"}
                        </Button>
                      ))}
                    </Stack>
                  ) : (
                    <Alert
                      severity="info"
                      action={canCreateCustomer ? <Button color="inherit" size="small" onClick={() => { setCreateError(""); setCreateDuplicate(null); setCreateOpen(true); }}>Crear cliente</Button> : null}
                    >
                      No hay clientes en el filtro actual. Creá uno o usá la búsqueda para encontrarlo.
                    </Alert>
                  )}
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                    La lista usa los clientes cargados abajo. Podés buscar por nombre, email, teléfono o documento.
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            {membershipFocus === "renew" ? (
              <Alert severity="info" onClose={() => setMembershipFocus("")} sx={{ py: .35 }}>
                Abrí la ficha de una membresía vencida o próxima a vencer y usá “Renovar”. La renovación conserva la trazabilidad y crea un período independiente.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {showMembershipWorkspace ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <Stack spacing={1.2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
                <Box>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Resumen operativo de membresías</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Cada indicador abre este mismo listado con el filtro correspondiente. Vigencia y cobro se calculan por separado. Próxima a vencer usa el umbral configurado de {membershipExpiringDays} días.</Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                variant="text"
                onClick={() => { setMembershipStatus("all"); setMembershipPaymentStatus("all"); setMembershipUsageStatus("all"); setPage(0); }}
                sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
              >
                Ver todas
              </Button>
            </Stack>

            <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
              {[
                ["active", "Vigentes", membershipSummary.active, "all"],
                ["expiring", "Próximas a vencer", membershipSummary.expiring, "all"],
                ["expired", "Vencidas", membershipSummary.expired, "all"],
                ["all", "Pagos pendientes", membershipPaymentSummary.unpaid, "unpaid"],
                ["recent-renewals", "Renovaciones · 30 días", membershipSummary.recentRenewals, "all"],
              ].map(([statusValue, label, count, paymentValue]) => {
                const selected = membershipStatus === statusValue && membershipPaymentStatus === paymentValue;
                return (
                  <Button
                    key={`${statusValue}-${paymentValue}`}
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    onClick={() => { setMembershipStatus(statusValue); setMembershipPaymentStatus(paymentValue); setPage(0); }}
                    sx={{ minHeight: 34, fontWeight: 900, whiteSpace: "nowrap" }}
                  >
                    {label} · {count || 0}
                  </Button>
                );
              })}
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={.8} alignItems={{ md: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>Otros estados:</Typography>
                {[
                  ["paused", "Pausadas", membershipSummary.paused, "warning"],
                  ["future", "Futuras", membershipSummary.future, "default"],
                ].map(([value, label, count, color]) => (
                  <Chip
                    key={value}
                    clickable
                    size="small"
                    color={color}
                    variant={membershipStatus === value && membershipPaymentStatus === "all" ? "filled" : "outlined"}
                    label={`${label} · ${count || 0}`}
                    onClick={() => { setMembershipStatus(value); setMembershipPaymentStatus("all"); setPage(0); }}
                    sx={{ fontWeight: 800 }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>Cobro:</Typography>
                {[
                  ["all", "Todos", null, "default"],
                  ["administrative", "Administrativo", membershipPaymentSummary.administrative, "info"],
                  ["accounted", "En Gestión", membershipPaymentSummary.accounted, "success"],
                ].map(([value, label, count, color]) => (
                  <Chip
                    key={`payment-${value}`}
                    clickable
                    size="small"
                    color={color}
                    variant={membershipPaymentStatus === value ? "filled" : "outlined"}
                    label={count === null ? label : `${label} · ${count || 0}`}
                    onClick={() => { setMembershipPaymentStatus(value); setPage(0); }}
                    sx={{ fontWeight: 800 }}
                  />
                ))}
              </Stack>
            </Stack>
            {membershipView ? (
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>Uso:</Typography>
                {[
                  ["all", "Todos", membershipWorkspacePagination.total, "default"],
                  ["available", "Con cupo", membershipUsageSummary.available, "success"],
                  ["exhausted", "Agotadas", membershipUsageSummary.exhausted, "warning"],
                  ["unlimited", "Sin límite", membershipUsageSummary.unlimited, "default"],
                ].map(([value, label, count, color]) => (
                  <Chip
                    key={`usage-${value}`}
                    clickable
                    size="small"
                    color={color}
                    variant={membershipUsageStatus === value ? "filled" : "outlined"}
                    label={`${label} · ${count || 0}`}
                    onClick={() => { setMembershipUsageStatus(value); setPage(0); }}
                    sx={{ fontWeight: 800 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {expirationView ? (
        <MembershipReminderPanel
          data={membershipReminders}
          loading={membershipRemindersLoading}
          canManage={canManageMemberships}
          onOpen={openMembershipReminder}
          onRenew={renewMembershipReminder}
        />
      ) : null}

      {pricingView ? (
        <PriceListsManager
          priceLists={priceLists}
          canManage={canManagePriceLists}
          canAssign={canManagePricing}
          currency={projectCurrency}
          busy={priceListBusy}
          onCreate={openCreatePriceList}
          onEdit={openEditPriceList}
        />
      ) : null}

      {!membershipView ? (
        <Grid container spacing={1.5}>
          {[
            ["Total", summary.total, "default"],
            ["Activos", summary.active, "success"],
            ["Pendientes", summary.pending, "warning"],
            ["Suspendidos", summary.suspended, "error"],
          ].map(([label, value, color]) => (
            <Grid item xs={6} md={3} key={label}>
              <Card variant="outlined" sx={{ borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 900 }}>{label}</Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
                    <Chip size="small" color={color} label={label} sx={{ display: { xs: "none", sm: "inline-flex" }, fontWeight: 850 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.6, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Grid container spacing={1.2}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              inputRef={searchInputRef}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={membershipView ? "Buscar cliente, plan, código, email o documento" : "Buscar por nombre, email, teléfono o documento"}
              inputProps={{
                "aria-label": membershipView
                  ? "Buscar membresías por cliente, plan, código, email o documento"
                  : "Buscar clientes por nombre, email, teléfono o documento",
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
            />
          </Grid>
          {!membershipView ? (
            <>
              <Grid item xs={12} sm={4} md={2.2}>
                <TextField select fullWidth size="small" label="Estado" value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="pending">Pendientes</MenuItem>
                  <MenuItem value="suspended">Suspendidos</MenuItem>
                  <MenuItem value="deleted">Eliminados</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={2.2}>
                <TextField select fullWidth size="small" label="Preferencias" value={preference} onChange={(event) => { setPreference(event.target.value); setPage(0); }}>
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="email">Acepta emails</MenuItem>
                  <MenuItem value="whatsapp">Acepta WhatsApp</MenuItem>
                  <MenuItem value="none">Sin preferencias</MenuItem>
                </TextField>
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={7}>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                Los filtros de vigencia, cobro y uso están arriba. La búsqueda filtra directamente los períodos de membresía.
              </Typography>
            </Grid>
          )}
          {pricingView ? (
            <Grid item xs={12} sm={4} md={2.6}>
              <TextField select fullWidth size="small" label="Lista de precios" value={priceListFilter} onChange={(event) => { setPriceListFilter(event.target.value); setPage(0); }}>
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="base">Lista base / sin personal</MenuItem>
                <MenuItem value="inactive">Asignación inactiva</MenuItem>
                {priceLists.filter((list) => !list.isBase).map((list) => (
                  <MenuItem key={list.id} value={list.id}>{priceListLabel(list)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}
        </Grid>
      </Paper>

      {membershipView ? (
        membershipWorkspaceLoading && !membershipWorkspaceRows.length ? (
          <DashboardLoadingState label="Cargando membresías…" minHeight={280} />
        ) : !membershipWorkspaceRows.length ? (
          <Alert severity="info">
            {eligibleMembershipItems.length
              ? "Todavía no hay períodos de membresía que coincidan con los filtros. Asigná un tipo a un cliente para empezar."
              : "Todavía no hay tipos de membresía configurados para nuevas asignaciones."}
          </Alert>
        ) : isMobile ? (
          <Stack spacing={1.2}>
            {membershipWorkspaceRows.map((row) => {
              const meta = membershipMeta({ status: row.statusKey });
              const payment = membershipPaymentMeta(row.payment);
              const reversedIds = new Set((row.usageMovements || []).filter((movement) => movement.type === "reversal").map((movement) => movement.reversalOfMovementId).filter(Boolean));
              const reversible = [...(row.usageMovements || [])].reverse().find((movement) => movement.type === "consume" && !reversedIds.has(movement.id));
              const canUse = canManageMemberships && ["active", "expiring"].includes(row.statusKey) && row.usage?.controlled && !row.usage?.exhausted;
              return (
                <Card key={row.id} variant="outlined" sx={{ borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                        <Box>
                          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{row.customer?.name || "Cliente"}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.item?.name || "Membresía"}</Typography>
                        </Box>
                        <Chip size="small" color={meta.color} variant={meta.attention ? "filled" : "outlined"} label={meta.label} sx={{ fontWeight: 850 }} />
                      </Stack>
                      <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={`${formatMoney(row.value, row.currency)} / ${membershipPeriodLabel(row.period?.value, row.period?.unit)}`} />
                        <Chip size="small" color={payment.color} variant="outlined" label={payment.label} />
                        <Chip size="small" color={row.usage?.exhausted ? "warning" : "default"} variant="outlined" label={row.usage?.controlled ? `${row.usage.consumed}/${row.usage.included} ${row.usage.label} · ${row.usage.available} disp.` : "Sin límite de usos"} />
                        {row.statusKey === "future" && row.renewedFromMembershipId ? <Chip size="small" color="info" variant="outlined" label="Renovación programada" /> : null}
                      </Stack>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                        {formatDate(row.startDate)} → {formatDate(row.effectiveEndDate || row.endDate)}
                      </Typography>
                      <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" onClick={() => openCustomer(row.customer)}>Ver ficha</Button>
                        {canCollectMembershipPayments && !row.payment?.recorded && Number(row.value) > 0 ? <Button size="small" variant="outlined" onClick={() => openCustomer(row.customer, { collectMembershipId: row.id })}>Registrar cobro</Button> : null}
                        {canUse ? <Button size="small" variant="contained" startIcon={<AddCircleOutlineRoundedIcon />} onClick={() => openWorkspaceUsage(row)}>Registrar uso</Button> : null}
                        {canManageMemberships && reversible ? <Button size="small" variant="text" startIcon={<UndoRoundedIcon />} onClick={() => openWorkspaceUsage(row, "reverse", reversible)}>Anular último uso</Button> : null}
                        {canManageMemberships && ["expiring", "expired"].includes(row.statusKey) ? <Button size="small" variant="text" startIcon={<AutorenewRoundedIcon />} onClick={() => { setMembershipFocus("renew"); openCustomer(row.customer, { renewMembershipId: row.id }); }}>Renovar</Button> : null}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            <TablePagination
              component="div"
              count={membershipWorkspacePagination.total || 0}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página"
            />
          </Stack>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Plan / importe</TableCell>
                  <TableCell>Cobro</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell>Uso</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {membershipWorkspaceRows.map((row) => {
                  const meta = membershipMeta({ status: row.statusKey });
                  const payment = membershipPaymentMeta(row.payment);
                  const reversedIds = new Set((row.usageMovements || []).filter((movement) => movement.type === "reversal").map((movement) => movement.reversalOfMovementId).filter(Boolean));
                  const reversible = [...(row.usageMovements || [])].reverse().find((movement) => movement.type === "consume" && !reversedIds.has(movement.id));
                  const canUse = canManageMemberships && ["active", "expiring"].includes(row.statusKey) && row.usage?.controlled && !row.usage?.exhausted;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 34, height: 34, bgcolor: BRAND.orange, fontWeight: 950, fontSize: 12 }}>{customerInitials(row.customer)}</Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{row.customer?.name || "Cliente"}</Typography>
                            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{row.customer?.email || row.customer?.phone || row.customer?.document || "Sin contacto"}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 850 }}>{row.item?.name || "Membresía"}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{formatMoney(row.value, row.currency)} cada {membershipPeriodLabel(row.period?.value, row.period?.unit)}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" color={payment.color} variant="outlined" label={payment.label} sx={{ fontWeight: 800 }} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 750 }}>{formatDate(row.startDate)} → {formatDate(row.effectiveEndDate || row.endDate)}</Typography>
                        {row.effectiveEndDate && row.endDate && row.effectiveEndDate !== row.endDate ? <Typography variant="caption" sx={{ color: "warning.dark" }}>Original: {formatDate(row.endDate)}</Typography> : null}
                      </TableCell>
                      <TableCell>
                        {row.usage?.controlled ? (
                          <Stack spacing={.35}>
                            <Typography variant="body2" sx={{ fontWeight: 850 }}>{row.usage.consumed}/{row.usage.included} {row.usage.label}</Typography>
                            <Chip size="small" color={row.usage.exhausted ? "warning" : "success"} variant="outlined" label={row.usage.exhausted ? "Agotada" : `${row.usage.available} disponibles`} sx={{ alignSelf: "flex-start", fontWeight: 800 }} />
                          </Stack>
                        ) : <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Sin límite</Typography>}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={.35} alignItems="flex-start">
                          <Chip size="small" color={meta.color} variant={meta.attention ? "filled" : "outlined"} label={meta.label} sx={{ fontWeight: 850 }} />
                          {row.statusKey === "future" && row.renewedFromMembershipId ? <Typography variant="caption" sx={{ color: "info.main", fontWeight: 800 }}>Renovación programada</Typography> : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={.35} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          <Tooltip title="Abrir ficha"><IconButton size="small" aria-label={`Abrir ficha de ${customerActionContext(row.customer)}`} onClick={() => openCustomer(row.customer)}><OpenInNewRoundedIcon fontSize="small" /></IconButton></Tooltip>
                          {canCollectMembershipPayments && !row.payment?.recorded && Number(row.value) > 0 ? <Button size="small" variant="outlined" onClick={() => openCustomer(row.customer, { collectMembershipId: row.id })}>Cobrar</Button> : null}
                          {canUse ? <Button size="small" variant="contained" onClick={() => openWorkspaceUsage(row)}>Usar</Button> : null}
                          {canManageMemberships && reversible ? <Tooltip title="Anular último consumo"><IconButton size="small" aria-label={`Anular último consumo de ${customerActionContext(row.customer)}`} onClick={() => openWorkspaceUsage(row, "reverse", reversible)}><UndoRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                          {canManageMemberships && ["expiring", "expired"].includes(row.statusKey) ? <Button size="small" variant="outlined" startIcon={<AutorenewRoundedIcon />} onClick={() => { setMembershipFocus("renew"); openCustomer(row.customer, { renewMembershipId: row.id }); }}>Renovar</Button> : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={membershipWorkspacePagination.total || 0}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas por página"
            />
          </TableContainer>
        )
      ) : (
        <>
      {loading && !customers.length ? (
        <DashboardLoadingState label="Cargando clientes…" minHeight={280} />
      ) : !customers.length ? (
        <Alert severity="info">
          {summary.total === 0
            ? "Este negocio todavía no tiene clientes. Podés cargar el primero manualmente o recibirlo desde la web pública."
            : "No hay clientes que coincidan con los filtros actuales."}
        </Alert>
      ) : isMobile ? (
        <Stack spacing={1.3}>
          {customers.map((customer) => (
            <CustomerMobileCard
              key={customer.id}
              customer={customer}
              priceLists={priceLists}
              canManageStatus={canManageStatus}
              canManagePricing={canManagePricing}
              pricingEnabled={pricingView}
              membershipsEnabled={showMembershipWorkspace}
              canManageMemberships={canManageMemberships}
              busy={busyCustomerId === customer.id}
              onOpen={openCustomer}
              onPriceList={assignPriceList}
              onStatus={askStatusChange}
              onRenew={openRenewal}
              onAssign={openMembershipAssignment}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Registro / acceso</TableCell>
                <TableCell>Actividad</TableCell>
                {showMembershipWorkspace ? <TableCell sx={{ minWidth: 155 }}>Membresía</TableCell> : null}
                {pricingView ? <TableCell sx={{ minWidth: 210 }}>Lista de precios</TableCell> : null}
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => {
                const statusInfo = statusMeta(customer.status);
                const busy = busyCustomerId === customer.id;
                return (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, bgcolor: BRAND.orange, fontWeight: 950, fontSize: 13 }}>{customerInitials(customer)}</Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
                          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{customer.document || "Sin documento"}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={.3}>
                        <Stack direction="row" spacing={.6} alignItems="center"><MailOutlineRoundedIcon sx={{ fontSize: 15, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} /><Typography variant="body2">{customer.email || "Sin email"}</Typography></Stack>
                        <Stack direction="row" spacing={.6} alignItems="center"><PhoneRoundedIcon sx={{ fontSize: 15, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} /><Typography variant="body2">{customer.phone || "—"}</Typography></Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 750 }}>{formatDate(customer.createdAt)}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                        {customer.webAccess?.enabled === false ? "Carga manual · sin acceso web" : `Acceso ${formatDate(customer.lastLoginAt, true)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={.7} alignItems="center"><ShoppingBagRoundedIcon sx={{ fontSize: 17, color: BRAND.orange }} /><Typography sx={{ fontWeight: 850 }}>{customer.activity?.orderCount || 0} pedidos</Typography></Stack>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Última {formatDate(customer.lastActivityAt, true)}</Typography>
                    </TableCell>
                    {showMembershipWorkspace ? (
                      <TableCell>
                        <MembershipStatus customer={customer} canManage={canManageMemberships} onRenew={openRenewal} onAssign={openMembershipAssignment} />
                      </TableCell>
                    ) : null}
                    {pricingView ? (
                      <TableCell>
                        <PriceListSelect
                          compact
                          customer={customer}
                          priceLists={priceLists}
                          disabled={!canManagePricing || busy}
                          onChange={(value) => assignPriceList(customer, value)}
                        />
                        {customer.priceList && !customer.priceList.active ? <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 800 }}>Requiere reasignación</Typography> : null}
                      </TableCell>
                    ) : null}
                    <TableCell><Chip size="small" color={statusInfo.color} label={statusInfo.label} sx={{ fontWeight: 850 }} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={.5} justifyContent="flex-end">
                        <Tooltip title="Abrir ficha"><IconButton size="small" aria-label={`Abrir ficha de ${customerActionContext(customer)}`} onClick={() => openCustomer(customer)}><OpenInNewRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        {canManageStatus && customer.status !== "deleted" ? (
                          <Tooltip title={customer.status === "suspended" ? "Reactivar cuenta" : "Suspender cuenta"}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`${customer.status === "suspended" ? "Reactivar cuenta" : "Suspender cuenta"} de ${customerActionContext(customer)}`}
                                color={customer.status === "suspended" ? "success" : "error"}
                                disabled={busy}
                                onClick={() => askStatusChange(customer)}
                              >
                                {customer.status === "suspended" ? <CheckCircleRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
          />
        </TableContainer>
      )}

      {isMobile && customers.length ? (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
        />
      ) : null}

        </>
      )}

      <ManualCustomerDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        duplicate={createDuplicate}
        allowMinimalIdentity
        onClose={() => { if (!createBusy) { setCreateOpen(false); setCreateError(""); setCreateDuplicate(null); } }}
        onSubmit={createManualCustomer}
        onReviewDuplicate={reviewDuplicateCustomer}
        onClearDuplicate={() => setCreateDuplicate(null)}
      />

      <ManualCustomerProfileDialog
        open={profileEditOpen}
        customer={detail?.customer}
        busy={profileEditBusy}
        error={profileEditError}
        onClose={() => { if (!profileEditBusy) { setProfileEditOpen(false); setProfileEditError(""); } }}
        onSubmit={updateManualCustomerProfile}
      />

      <Dialog open={membershipPlanOpen} onClose={membershipPlanBusy ? undefined : () => setMembershipPlanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Crear tipo de membresía</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            <Alert severity="info">
              Este tipo queda guardado como un servicio reutilizable. El inicio, vencimiento y estado pertenecen a cada cliente cuando se lo asignás. No configura débito automático.
            </Alert>
            {membershipPlanError ? <Alert severity="error">{membershipPlanError}</Alert> : null}
            <TextField
              autoFocus
              fullWidth
              label="Nombre de la membresía"
              value={membershipPlanForm.name}
              onChange={(event) => setMembershipPlanForm((current) => ({ ...current, name: event.target.value }))}
              inputProps={{ maxLength: 120 }}
              placeholder="Ej. Plan mensual, mantenimiento, 8 clases"
            />
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Precio por período"
                  type="number"
                  value={membershipPlanForm.price}
                  onChange={(event) => setMembershipPlanForm((current) => ({ ...current, price: event.target.value }))}
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  fullWidth
                  label="Cada"
                  type="number"
                  value={membershipPlanForm.periodValue}
                  onChange={(event) => setMembershipPlanForm((current) => ({ ...current, periodValue: event.target.value }))}
                  inputProps={{ min: 1, max: 10000, step: 1 }}
                />
              </Grid>
              <Grid item xs={7} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Período"
                  value={membershipPlanForm.periodUnit}
                  onChange={(event) => setMembershipPlanForm((current) => ({ ...current, periodUnit: event.target.value }))}
                >
                  <MenuItem value="days">Días</MenuItem>
                  <MenuItem value="weeks">Semanas</MenuItem>
                  <MenuItem value="months">Meses</MenuItem>
                  <MenuItem value="years">Años</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Divider />
            <Box>
              <FormControlLabel
                control={<Checkbox checked={membershipPlanForm.usageEnabled} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, usageEnabled: event.target.checked }))} />}
                label="Controlar usos o consumos incluidos"
              />
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", display: "block", ml: 4 }}>
                Activá esto si cada período incluye una cantidad limitada de accesos, clases, sesiones, servicios o consumos.
              </Typography>
            </Box>
            {membershipPlanForm.usageEnabled ? (
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Cantidad incluida"
                    type="number"
                    value={membershipPlanForm.includedUses}
                    onChange={(event) => setMembershipPlanForm((current) => ({ ...current, includedUses: event.target.value }))}
                    inputProps={{ min: 1, max: 1000000, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    label="Cómo llamar a la unidad"
                    value={membershipPlanForm.usageLabel}
                    onChange={(event) => setMembershipPlanForm((current) => ({ ...current, usageLabel: event.target.value }))}
                    inputProps={{ maxLength: 60 }}
                    placeholder="usos, clases, sesiones, accesos…"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 800 }}>Sugerencias:</Typography>
                    {["usos", "accesos", "clases", "sesiones", "consumos"].map((label) => (
                      <Chip key={label} clickable size="small" label={label} variant={membershipPlanForm.usageLabel === label ? "filled" : "outlined"} onClick={() => setMembershipPlanForm((current) => ({ ...current, usageLabel: label }))} />
                    ))}
                  </Stack>
                </Grid>
                {Number.isInteger(Number(membershipPlanForm.includedUses)) && Number(membershipPlanForm.includedUses) > 0 ? (
                  <Grid item xs={12}><Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Ejemplo: Incluye {Number(membershipPlanForm.includedUses)} {String(membershipPlanForm.usageLabel || "usos")} por período.</Typography></Grid>
                ) : null}
              </Grid>
            ) : null}
            {String(membershipPlanForm.name || "").trim() && membershipPlanForm.price !== "" && Number.isFinite(Number(membershipPlanForm.price)) ? (
              <Alert severity="success">
                <strong>Antes de guardar:</strong> {String(membershipPlanForm.name).trim()} · {formatMoney(Number(membershipPlanForm.price), "ARS")} cada {membershipPeriodLabel(membershipPlanForm.periodValue, membershipPlanForm.periodUnit)}
                {membershipPlanForm.usageEnabled && Number(membershipPlanForm.includedUses) > 0 ? ` · incluye ${Number(membershipPlanForm.includedUses)} ${String(membershipPlanForm.usageLabel || "usos")} por período` : " · sin control de usos"}.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembershipPlanOpen(false)} disabled={membershipPlanBusy}>Cancelar</Button>
          <Button variant="contained" onClick={createMembershipPlan} disabled={membershipPlanBusy} startIcon={membershipPlanBusy ? <WebProfitSpinner size={16} /> : undefined}>
            {membershipPlanBusy ? "Creando…" : "Crear y seguir con asignación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(workspaceUsageAction)} onClose={workspaceUsageBusy ? undefined : () => setWorkspaceUsageAction(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {workspaceUsageAction?.action === "reverse" ? "Anular consumo" : "Registrar uso"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.3} sx={{ pt: .5 }}>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>{workspaceUsageAction?.row?.customer?.name || "Cliente"}</Typography>
              <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{workspaceUsageAction?.row?.item?.name || "Membresía"}</Typography>
            </Box>
            {workspaceUsageAction?.action === "reverse" ? (
              <Alert severity="warning">
                Se conservará el movimiento original y se agregará una reversión por {workspaceUsageAction?.movement?.quantity || 0} {workspaceUsageAction?.row?.usage?.label || "usos"}.
              </Alert>
            ) : (
              <>
                <Alert severity="info">
                  Disponible en este período: {workspaceUsageAction?.row?.usage?.available ?? 0} {workspaceUsageAction?.row?.usage?.label || "usos"}.
                </Alert>
                <TextField
                  fullWidth
                  autoFocus
                  type="number"
                  label="Cantidad a consumir"
                  value={workspaceUsageAction?.quantity || ""}
                  onChange={(event) => setWorkspaceUsageAction((current) => current ? { ...current, quantity: event.target.value } : current)}
                  inputProps={{ min: 1, max: workspaceUsageAction?.row?.usage?.available || 1, step: 1 }}
                />
              </>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Nota interna (opcional)"
              value={workspaceUsageAction?.note || ""}
              onChange={(event) => setWorkspaceUsageAction((current) => current ? { ...current, note: event.target.value } : current)}
              inputProps={{ maxLength: 300 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkspaceUsageAction(null)} disabled={workspaceUsageBusy}>Cancelar</Button>
          <Button variant="contained" color={workspaceUsageAction?.action === "reverse" ? "warning" : "primary"} onClick={submitWorkspaceUsage} disabled={workspaceUsageBusy} startIcon={workspaceUsageBusy ? <WebProfitSpinner size={16} /> : undefined}>
            {workspaceUsageBusy ? "Guardando…" : workspaceUsageAction?.action === "reverse" ? "Anular consumo" : "Registrar uso"}
          </Button>
        </DialogActions>
      </Dialog>

      <PriceListEditorDialog
        open={Boolean(priceListDialog)}
        priceList={priceListDialog?.priceList || null}
        currency={projectCurrency}
        busy={priceListBusy}
        error={priceListError}
        onClose={() => { if (!priceListBusy) { setPriceListDialog(null); setPriceListError(""); } }}
        onSubmit={savePriceList}
      />

      <CustomerDetailDialog
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        priceLists={priceLists}
        projectId={projectId}
        projectTimezone={effectiveProject?.timezone}
        membershipsEnabled={membershipsEnabled}
        canManageMemberships={canManageMemberships}
        canCollectMembershipPayments={canCollectMembershipPayments}
        canEditManualProfile={canManageStatus && detail?.customer?.origin === "manual" && detail?.customer?.webAccess?.enabled === false}
        onEditProfile={openManualProfileEdit}
        renewMembershipId={renewMembershipId}
        collectMembershipId={collectMembershipId}
        onMembershipChanged={refreshCrm}
        canManageStatus={canManageStatus}
        canManagePricing={canManagePricing}
        pricingEnabled={canReadPriceLists}
        busy={Boolean(busyCustomerId)}
        onClose={() => { setDetailOpen(false); setDetail(null); setRenewMembershipId(""); setCollectMembershipId(""); setProfileEditOpen(false); setProfileEditError(""); }}
        onPriceList={assignPriceList}
        onStatus={askStatusChange}
      />

      <Dialog open={Boolean(statusPrompt)} onClose={busyCustomerId ? undefined : () => setStatusPrompt(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {statusPrompt?.nextStatus === "suspended" ? "Suspender cliente" : "Reactivar cliente"}
        </DialogTitle>
        <DialogContent>
          <Alert severity={statusPrompt?.nextStatus === "suspended" ? "warning" : "info"} sx={{ mb: 1.5 }}>
            {statusPrompt?.nextStatus === "suspended"
              ? "El cliente quedará suspendido en el CRM y, si tiene acceso web, tampoco podrá iniciar sesión. Su historial se conserva."
              : "El cliente volverá a estar activo en el CRM y, si tiene acceso web, podrá usarlo normalmente."}
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Motivo interno (opcional)"
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusPrompt(null)} disabled={Boolean(busyCustomerId)}>Cancelar</Button>
          <Button
            variant="contained"
            color={statusPrompt?.nextStatus === "suspended" ? "error" : "success"}
            onClick={confirmStatusChange}
            disabled={Boolean(busyCustomerId)}
          >
            {busyCustomerId ? <WebProfitSpinner size={20} /> : statusPrompt?.nextStatus === "suspended" ? "Suspender" : "Reactivar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
